import { useEffect, useState, useMemo, useRef } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { useCachedState, cacheGet } from "@/lib/swr";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "./ctx";
import { Palette, Inbox,
  LayoutDashboard, MessageSquareText, MessagesSquare, FileText, Users, ClipboardList, ClipboardCheck,
  Settings2, Building2, LogOut, ChevronDown, ChevronRight, Phone, UserCog, Menu, X,
  Headset, TrendingUp, ArrowLeft, Mic, FileBarChart, Eye, Bell, Settings, Scale, BrainCircuit, Landmark, Wallet, History, CalendarCheck, BarChart3, CalendarDays, CalendarClock, Contact, UserRound, Search, PenLine, Sparkles, UserPlus, Check, SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CustomizeMenuDialog } from "@/components/CustomizeMenuDialog";
import { CommandPalette } from "@/components/CommandPalette";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { BrandFont } from "@/components/BrandFont";
import { navBrand, useBrandTheme } from "@/lib/brand";
import { V, setActiveVocab } from "@/lib/vocab";
import { MessageSquareHeart, Globe, ScrollText, Siren, ShieldCheck, HardHat, AlertTriangle, Lightbulb, Layers, Activity } from "lucide-react";
import { PulseDot } from "@/components/pulse/PulseDot";
import { PortfolioManagerPicker } from "@/components/PortfolioManagerPicker";
import { SeatPicker } from "@/components/SeatPicker";
import { useSidebarCollapse } from "@/hooks/useSidebarCollapse";
import { SidebarCollapseToggle, SidebarHint } from "@/components/SidebarCollapse";

// Super-admin "View as Community Manager": the backend downgrades the session per request when
// these headers are present (deps._apply_preview). Applied at import so the very first /tenants
// call already reflects the preview after a refresh.
const PP_KEY = "askredline.portfolioPersona";
function applyPreviewHeaders(persona, companyId) {
  const h = api.defaults.headers.common;
  if (persona && companyId) {
    h["X-Preview-As"] = "regional_manager";
    h["X-Preview-Company"] = companyId;
    h["X-Preview-Tenants"] = (persona.tenant_ids || []).join(",");
  } else {
    delete h["X-Preview-As"]; delete h["X-Preview-Company"]; delete h["X-Preview-Tenants"];
  }
}
function readJson(key) { try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch { return null; } }
applyPreviewHeaders(readJson(PP_KEY), readJson("askredline.viewCompany")?.id);

// Redline company console (super-admin) — running the service across all communities
const COMPANY_NAV = [
  { to: "/dashboard/management-companies", icon: Landmark, label: "Portfolios", group: "Portfolios" },
  { to: "/dashboard/communities", icon: Building2, label: "Communities", group: "Portfolios" },
  { to: "/dashboard/operator", icon: Headset, label: "Operator Desk", group: "Operations" },
  { to: "/dashboard/vendor-directory", icon: HardHat, label: "Trusted Vendors", group: "Operations" },
  { to: "/dashboard/voice-demo", icon: Mic, label: "Voice & SMS Simulator", group: "Operations" },
  { to: "/dashboard/admin-pulse", icon: Activity, label: "Pulse", group: "Insights" },
  { to: "/dashboard/feedback", icon: MessageSquareHeart, label: "Feedback", group: "Insights", adminCountKey: "platform_feedback_new", tone: "grey", countTitle: "New feedback about the AI", badgeId: "platform-feedback-new-badge" },
  { to: "/dashboard/reports", icon: FileBarChart, label: "Platform satisfaction", group: "Insights" },
  { to: "/dashboard/traffic", icon: TrendingUp, label: "Traffic", group: "Insights" },
  { to: "/dashboard/billing", icon: Wallet, label: "Billing & Plans", group: "Business" },
  { to: "/dashboard/team", icon: UserCog, label: "Team", group: "Business" },
  { to: "/dashboard/industries", icon: Layers, label: "Industries", group: "Product setup" },
  { to: "/dashboard/appearance", icon: Palette, label: "Appearance", group: "Product setup", superOnly: true },
  { to: "/dashboard/numbers", icon: Phone, label: "Numbers & Opt-Ins", group: "Product setup" },
  { to: "/dashboard/platform-rules", icon: ScrollText, label: "How Redline works", group: "Product setup" },
  { to: "/dashboard/voice-health", icon: Activity, label: "Voice Health", group: "System health", superOnly: true },
  { to: "/dashboard/brain-diagnostic", icon: Search, label: "Brain Diagnostic", group: "System health" },
];
const COMPANY_PATHS = COMPANY_NAV.map((n) => n.to);
const COMPANY_GROUPS = ["Portfolios", "Operations", "Insights", "Business", "Product setup", "System health"];
// iter252/H6 — every role can always reach its own account settings. The routing guards
// used to bounce platform/onboarding admins and operators off /dashboard/account.
// /dashboard/notifications is kept here ON PURPOSE: the standalone page was retired (iter301)
// and the path now route-redirects to /dashboard/account#notifications — this entry stops the
// mode guards from hijacking it before that redirect fires. Do not remove.
const ALWAYS_ALLOWED_PATHS = ["/dashboard/account", "/dashboard/notifications"];
// Legacy URLs that now just <Navigate> to a consolidated tab (see App.js). They were removed
// from the nav arrays, so the mode guards below would hijack them (bouncing to /communities or
// /portfolio) before the redirect fires — skip them so the route-level redirect can land.
const LEGACY_REDIRECT_PATHS = ["/dashboard/find-a-vendor", "/dashboard/vendor-roles"];
// Pages that belong to the owner (money) or to portfolio/insight work the onboarding
// role has no business in. Hiding them beats linking to a permission error.
const OWNER_ONLY_PATHS = ["/dashboard/billing", "/dashboard/industries", "/dashboard/appearance"];
const ONBOARDING_HIDDEN_PATHS = ["/dashboard/management-companies", "/dashboard/intelligence",
                                 "/dashboard/traffic", "/dashboard/operator", "/dashboard/feedback", "/dashboard/product-signals",
                                 "/dashboard/brain-diagnostic", "/dashboard/voice-demo",
                                 "/dashboard/vendor-directory", "/dashboard/admin-pulse"];
// Multi-community managers: "Mine → Pulse" — the only cross-community page they get. Everything
// else stays inside one community (it is called PULSE everywhere, never "My communities").
const MINE_NAV = [{ to: "/dashboard/mine/pulse", icon: Activity, label: "Pulse", group: "Mine", pulseBadge: true, badgeId: "mine-pulse-badge", countTitle: "Communities in the red" }];
const MINE_ALLOWED_PATHS = ["/dashboard/mine/pulse", "/dashboard/account", "/dashboard/notifications"];

// A single community's workspace (HOA managers/board, or super-admin acting on behalf)
// Grouped for clarity: daily work up top, then Community data, then Settings.
// `group` = classic menu section · `group2` = the v2 menu (Inbox · Operations · Communications ·
// Knowledge · Records · Settings) · `countKey`/`tone` = which live count badges the item.
// Tones mean one thing each: red = urgent, amber = a person is waiting on you, rose = overdue,
// grey = informational, dot = a live emergency.
const communityNavFactory = () => [
  { to: "/dashboard", end: true, icon: LayoutDashboard, label: "Overview", area: "overview", group: "Daily work", group2: "Pinned", order2: 0 },
  { to: "/dashboard/copilot", icon: Sparkles, label: "Ask Copilot", area: "copilot", copilotGate: true, group: "Daily work", group2: "Pinned", order2: 1 },
  { to: "/dashboard/actions", vkey: "requests", icon: ClipboardList, label: "Requests", area: "requests", labelKey: "requests_label", modeGate: "answers_requests", group: "Daily work", group2: "Inbox", order2: 1, countKey: "requests_urgent", tone: "red", countTitle: "Urgent/high open requests", badgeId: "requests-urgent-badge" },
  { to: "/dashboard/conversations", vkey: "conversations", icon: MessagesSquare, label: "Conversation History", label2: "Conversations", area: "conversations", feature: "conversations", group: "Records", group2: "Inbox", order2: 2, countKey: "conversations_waiting", tone: "amber", countTitle: "Threads waiting on a person", badgeId: "conversations-waiting-badge" },
  { to: "/dashboard/mail", vkey: "mail", icon: Inbox, label: "Mailbox", area: "mail", feature: "mail", mailGate: true, copilotTag: true, group: "Records", group2: "Inbox", order2: 3, countKey: "mail_new", tone: "amber", countTitle: "Unread emails and decisions waiting in the Mailbox", badgeId: "mail-new-badge" },
  { to: "/dashboard/documents", vkey: "documents", icon: FileText, label: "AI Documents", area: "documents", feature: "knowledge", group: "Community", group2: "Knowledge", order2: 1, countKey: "knowledge_suggestions", tone: "amber", countTitle: `${V.one("person")} questions waiting in "Teach your assistant"`, badgeId: "knowledge-suggestion-badge" },
  { to: "/dashboard/websites", vkey: "websites", icon: Globe, label: "Website Knowledge", area: "documents", labelKey: "websites_label", feature: "websites", group: "Community", group2: "Knowledge", order2: 2 },
  { to: "/dashboard/rules", vkey: "rules", icon: Scale, label: "Rules", area: "documents", feature: "rules", group: "Community", group2: "Knowledge", order2: 3 },
  { to: "/dashboard/field-services", vkey: "field_services", icon: HardHat, label: "Field Services", area: "field_services", labelKey: "field_services_label", entGate: "field_services", group: "Community tools", group2: "Operations", order2: 1, countKey: "field_services_attention", tone: "amber", countTitle: "Jobs with no answer or overdue, plus invoices awaiting approval", badgeId: "field-services-attention-badge" },
  { to: "/dashboard/checklists", vkey: "checklists", icon: ClipboardCheck, label: "Checklists", area: "field_services", group: "Community tools", group2: "Operations", order2: 2, countKey: "checklists_attention", tone: "amber", countTitle: "Checklist visits waiting on you — submitted, vendor replied, or nobody showed up", badgeId: "checklists-attention-badge" },
  { to: "/dashboard/amenities", vkey: "reservations", icon: CalendarCheck, label: "Reservations", area: "reservations", labelKey: "reservations_label", entGate: "amenities", group: "Community tools", group2: "Operations", order2: 2, countKey: "reservations_pending", tone: "amber", countTitle: "Reservations waiting for approval", badgeId: "reservations-pending-badge" },
  { to: "/dashboard/renewals", vkey: "renewals", icon: CalendarClock, label: "Renewals", area: "renewals", feature: "renewals", group: "Community tools", group2: "Operations", order2: 3, countKey: "renewals_overdue", tone: "rose", countTitle: "Obligations past due", badgeId: "renewals-overdue-badge" },
  { to: "/dashboard/emergency", vkey: "emergency", icon: Siren, label: "Emergency Alerts", area: "emergency", labelKey: "emergency_label", entGate: "emergency_escalation", group: "Community tools", group2: "Operations", order2: 4, countKey: "emergency_active", tone: "dot", countTitle: "An emergency is open right now", badgeId: "emergency-active-dot" },
  { to: "/dashboard/contacts", vkey: "contacts", icon: Contact, label: "Trusted Vendors", area: "documents", feature: "contacts", group: "Community", group2: "Operations", order2: 5 },
  { to: "/dashboard/signatures", vkey: "signatures", icon: PenLine, label: "E-Sign", area: "signatures", labelKey: "signatures_label", entGate: "signatures", group: "Community tools", group2: "Operations", order2: 6, countKey: "signatures_awaiting", tone: "grey", countTitle: "Documents out for signature", badgeId: "signatures-awaiting-badge" },
  { to: "/dashboard/broadcasts", vkey: "broadcasts", icon: MessageSquareText, label: "Messages", area: "messages", labelKey: "broadcasts_label", entGate: "broadcasts", boardGrant: "can_broadcast", group: "Community tools", group2: "Outreach", order2: 1 },
  { to: "/dashboard/events", vkey: "events", icon: CalendarDays, label: "Events", area: "events", feature: "events", group: "Community", group2: "Outreach", order2: 2 },
  { to: "/dashboard/surveys", vkey: "surveys", icon: BarChart3, label: "Surveys", area: "surveys", labelKey: "surveys_label", entGate: "surveys", group: "Community tools", group2: "Outreach", order2: 3 },
  { to: "/dashboard/reports", vkey: "reports", icon: FileBarChart, label: "Reports", area: "reports", feature: "reports", group: "Community", group2: "Reports & records", order2: 1 },
  { to: "/dashboard/feedback", vkey: "feedback", icon: MessageSquareHeart, label: "Feedback", area: "feedback", feature: "feedback", group: "Records", group2: "Reports & records", order2: 2, countKey: "feedback_new", tone: "grey", countTitle: "New feedback this week", badgeId: "feedback-new-badge" },
  { to: "/dashboard/roster", vkey: "roster", icon: Users, label: `${V.many("person")}`, labelKey: "roster_label", area: "roster", feature: "roster", group: "Community", group2: "Reports & records", order2: 3, countKey: "roster_pending", tone: "amber", countTitle: `${V.many("person")} waiting for the office to confirm they live here`, badgeId: "roster-pending-badge" },
  { to: "/dashboard/activity", icon: History, label: "Activity Log", area: "any", feature: "activity", group: "Records", group2: "Reports & records", order2: 4 },
  { to: "/dashboard/community", vkey: "profile", icon: Building2, label: `${V.one("org")} Profile`, area: "profile", feature: "profile", group: "Settings", group2: "Settings", order2: 1 },
  { to: "/dashboard/settings", icon: Settings2, label: "System Settings", area: "settings", feature: "agent_config", requiresConfig: true, superAlways: true, group: "Settings", group2: "Settings", order2: 3 },
  { to: "/dashboard/team", vkey: "team", icon: UserCog, label: "Team", area: "settings", managerUp: true, boardToo: true, companyHide: true, feature: "team", group: "Settings", group2: "Settings", order2: 4 },
  { to: "/dashboard/public-page", vkey: "public_page", icon: Globe, label: "Public Page", area: "settings", feature: "public_page", group: "Settings", group2: "Settings", order2: 2 },
];
const COMMUNITY_GROUPS = ["Daily work", "Community tools", "Community", "Records", "Settings"];
// v2 menu — "Home" renders without a header (Overview · Ask Copilot pinned at the top).
const COMMUNITY_GROUPS_V2 = ["Pinned", "Inbox", "Knowledge", "Operations", "Outreach", "Reports & records", "Settings"];
const navGroupDescriptionsFactory = () => ({
  Inbox: `${V.a("person")} is waiting on a person`, Knowledge: "what the concierge answers from",
  Operations: "running the property", Outreach: "things you send or publish",
  "Reports & records": "look something up", Settings: "configure & test",
});
const COMMUNITY_TOOLS = [
  { to: "/dashboard/simulator", icon: MessageSquareText, label: "Live Demo", area: "settings", feature: "simulator", group: "Tools", group2: "Settings", order2: 5 },
];
const BADGE_TONE = { red: "bg-redline-500 text-white", amber: "bg-amber-500 text-white", rose: "bg-rose-500 text-white", grey: "bg-white/15 text-sand-100" };

// Management-company portfolio console (company_admin / regional_manager) — scoped to their communities.
// A factory, not a const: the "Communities" row has to speak the firm's own word (Firms, Properties…)
// when the whole portfolio runs one industry pack (iter508).
const portfolioNavFactory = () => [
  { to: "/dashboard/portfolio", icon: LayoutDashboard, label: "Overview", group: "Pinned" },
  { to: "/dashboard/portfolio-pulse", icon: Activity, label: "Pulse", group: "Portfolio", anyPulse: true, pulseBadge: true, badgeId: "portfolio-pulse-badge", countTitle: `${V.many("org")} in the red` },
  { to: "/dashboard/portfolio-communities", icon: Building2, label: V.many("org"), group: "Portfolio" },
  { to: "/dashboard/vendor-directory", icon: HardHat, label: "Trusted Vendors", group: "Portfolio", anyFieldServices: true },
  { to: "/dashboard/team", icon: UserCog, label: "Team", group: "Company", companyStaff: true },
  { to: "/dashboard/roles", icon: ShieldCheck, label: "Roles", group: "Company", companyAdminOnly: true },
  { to: "/dashboard/portfolio-settings", icon: Settings2, label: "Settings", group: "Company", companyAdminOnly: true },
];
const PORTFOLIO_PATHS = portfolioNavFactory().map((n) => n.to);
const PORTFOLIO_GROUPS = ["Pinned", "Portfolio", "Company"];

// Merge the entitled nav items with a saved custom layout into an ordered {group: [items]} map.
// Sections stay in their fixed default order; saved item order/section wins; any new or
// not-yet-placed item falls back to its default section; gated-out items are simply skipped.
function buildArrangement(navItems, groupOrder, layout, groupOf = (n) => n.group) {
  const byTo = new Map(navItems.map((n) => [n.to, n]));
  const itemsByGroup = {};
  groupOrder.forEach((g) => { itemsByGroup[g] = []; });
  const placed = new Set();
  if (layout && Array.isArray(layout.groups)) {
    for (const g of layout.groups) {
      if (!itemsByGroup[g.id]) continue;
      for (const to of (g.items || [])) {
        if (byTo.has(to) && !placed.has(to)) { itemsByGroup[g.id].push(byTo.get(to)); placed.add(to); }
      }
    }
  }
  for (const n of navItems) {
    if (placed.has(n.to)) continue;
    const dg = groupOf(n);
    const g = itemsByGroup[dg] ? dg : groupOrder[0];
    itemsByGroup[g].push(n); placed.add(n.to);
  }
  return itemsByGroup;
}

const NAV_COUNT_KEYS = ["requests_urgent", "conversations_waiting", "mail_new", "emergency_active", "feedback_new", "field_services_attention", "checklists_attention",
  "reservations_pending", "signatures_awaiting", "renewals_overdue", "knowledge_suggestions", "roster_pending"];
const EMPTY_COUNTS = Object.fromEntries(NAV_COUNT_KEYS.map((k) => [k, null]));

export default function DashboardLayout() {
  const NAV_GROUP_DESCRIPTIONS = navGroupDescriptionsFactory();
  const COMMUNITY_NAV = communityNavFactory();
  const { user, logout, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tenants, setTenants] = useCachedState("tenants", []);
  const [activeTenantId, setActiveTenantIdState] = useState(() => {
    try { return localStorage.getItem("askredline.activeTenant") || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(() => cacheGet("tenants") === undefined);
  const [tenantsFresh, setTenantsFresh] = useState(false); // true once /tenants answered for THIS session (cache may be another user's)
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, toggleSidebarCollapsed] = useSidebarCollapse();
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  // iter386 audit — status banners live on Overview ONLY. Everywhere else the sidebar badges, the bell and
  // each page's own state carry the same signals; stacking them on every screen was pure noise.
  const onOverview = location.pathname.replace(/\/+$/, "") === "/dashboard";
  const quietPage = !onOverview;
  const [smsBannerHidden, setSmsBannerHidden] = useState(() => sessionStorage.getItem("askredline.smsBannerHidden") === "1");
  const [notifs, setNotifs] = useState([]);
  const [expandedTypes, setExpandedTypes] = useState([]);
  const [unread, setUnread] = useState(0);
  const [emStatus, setEmStatus] = useState(null);
  const [navLayout, setNavLayout] = useState(null);
  const [navPreset, setNavPreset] = useState("classic");
  const [navPresetSource, setNavPresetSource] = useState("default");
  const [platformNavDefault, setPlatformNavDefault] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  const [replayTour, setReplayTour] = useState(0);

  const setActiveTenantId = (id) => {
    setActiveTenantIdState(id);
    // Persist so a page refresh keeps you inside the same community/tab instead of bouncing to the top.
    try { if (id) localStorage.setItem("askredline.activeTenant", id); else localStorage.removeItem("askredline.activeTenant"); } catch { /* ignore */ }
  };

  const rawRole = user?.role;
  const isCustom = rawRole === "custom";
  const customPerms = user?.custom_perms || {};
  // A custom-role login runs inside the community console like a manager; the per-area
  // permission filter below decides which pages it actually sees. Aliasing keeps all the
  // existing hoa_manager console plumbing (switcher, community mode) working.
  const role = isCustom ? "hoa_manager" : rawRole;
  // "any" = visible to a custom role that can see at least one area (the Activity Log — it records who did what).
  const areaAllowed = (area) => !isCustom || !area
    || (area === "any" ? Object.values(customPerms).some((v) => ["view", "edit"].includes(v)) : ["view", "edit"].includes(customPerms[area]));
  const realIsSuper = ["super_admin", "platform_admin"].includes(role);
  // "Viewing as" from the Redline console: which seat picker is open (administrator · pm · manager · board).
  const [seatPick, setSeatPick] = useState(null);
  const [pmCompany, setPmCompany] = useState(null); // Community Manager preview, step 2: which company
  // "View as" persona: super admin previews a community as one of its user types.
  // null = normal; "hoa_manager" | "board_member" = client-side role swap (fully interactive,
  // requests still run with the super-admin session which is a permission superset).
  const [viewPersona, setViewPersona] = useState(null);
  const viewAsUser = !!viewPersona;
  // super-admin "view as management company" — persisted so a refresh inside the portfolio (or inside one
  // of its communities) doesn't silently drop you back to the Redline console.
  const pendingPortfolioPath = useRef(null); // destination requested by enterCompanyView / exitCommunity (see routing guard)
  const [viewCompany, setViewCompanyState] = useState(() => {
    try { const raw = localStorage.getItem("askredline.viewCompany"); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const setViewCompany = (c) => {
    setViewCompanyState(c);
    try { if (c) localStorage.setItem("askredline.viewCompany", JSON.stringify(typeof c === "string" ? { id: c } : { id: c.id, name: c.name })); else localStorage.removeItem("askredline.viewCompany"); } catch { /* ignore */ }
    if (!c) setPortfolioPersona(null);
  };
  // "View as Community Manager" (super admin inside a portfolio): { tenant_ids: [...] } — persisted like viewCompany.
  const [portfolioPersona, setPortfolioPersonaState] = useState(() => readJson(PP_KEY));
  const [portfolioPickerOpen, setPortfolioPickerOpen] = useState(false);
  const setPortfolioPersona = (p, companyId) => {
    setPortfolioPersonaState(p);
    try { if (p) localStorage.setItem(PP_KEY, JSON.stringify(p)); else localStorage.removeItem(PP_KEY); } catch { /* ignore */ }
    applyPreviewHeaders(p, companyId);
  };
  const isSuper = ["super_admin", "platform_admin", "onboarding_admin"].includes(role);
  const isOwner = role === "super_admin";
  const isOperator = role === "operator";
  const isCompany = role === "company_admin" || role === "regional_manager";
  const isManager = role === "hoa_manager" || role === "board_member";
  const actingCompany = isCompany || (isSuper && !!viewCompany);
  // iter431 — the PORTFOLIO's identity (a management company's name, logo, colour, font). Company
  // roles get it from /auth/me; a super admin viewing a company pulls it. Worn by the shell
  // between communities; the switcher and the "back" link name the firm inside one.
  const portfolioId = isCompany ? (user?.company_id || null)
    : (isSuper && viewCompany) ? (typeof viewCompany === "string" ? viewCompany : viewCompany?.id) : null;
  const [companyBrand, setCompanyBrand] = useState(null);
  useEffect(() => {
    if (!portfolioId) { setCompanyBrand(null); return; }
    let alive = true;
    const pull = () => api.get(`/companies/${portfolioId}/brand`).then((r) => { if (alive) setCompanyBrand(r.data.console); }).catch(() => {});
    pull();
    window.addEventListener("askredline:company-brand-updated", pull);
    return () => { alive = false; window.removeEventListener("askredline:company-brand-updated", pull); };
  }, [portfolioId]);
  const portfolio = companyBrand || (isCompany ? user?.company : null) || null;
  const portfolioName = portfolio?.name || viewCompany?.name || "";

  const refreshTenants = async (honourLink = false) => {
    const res = await api.get("/tenants");
    setTenants(res.data);
    setTenantsFresh(true);
    // A deep link from an alert text/email/bell names its community (?tenant=) — land there, not
    // in whichever community this browser was last in (iter479d). Honoured once, on first load only,
    // so a later brand refresh can't snap a manager back after they've switched communities.
    const linked = honourLink ? new URLSearchParams(window.location.search).get("tenant") : null;
    const linkedOk = !!linked && res.data.some((t) => t.id === linked);
    const cur = linkedOk ? linked : activeTenantId;
    if (linkedOk && linked !== activeTenantId) { setViewPersona(null); setActiveTenantId(linked); }
    if (isManager) {
      // A manager running 2+ communities lands on Mine → Pulse and picks a community from there;
      // single-community managers (and board seats) go straight into their community.
      const valid = cur && res.data.some((t) => t.id === cur);
      const multi = rawRole === "hoa_manager" && res.data.length > 1;
      if (!valid) { if (multi) setActiveTenantIdState(null); else setActiveTenantId(user?.tenant_id || res.data[0]?.id || null); }
    }
    // Drop a persisted community that no longer exists (e.g. deleted) so we don't get stuck.
    else if ((isSuper || isCompany) && cur && !res.data.some((t) => t.id === cur)) {
      setActiveTenantId(null);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshTenants(true); }, []);
  // A community's logo, colour and font drive this shell (iter337), so a save in Community
  // Profile has to repaint it straight away rather than on the next full page load.
  useEffect(() => {
    const onBrandUpdate = () => { setLogoBroken(false); refreshTenants(); };
    window.addEventListener("askredline:brand-updated", onBrandUpdate);
    window.addEventListener("askredline:company-brand-updated", onBrandUpdate);
    return () => {
      window.removeEventListener("askredline:brand-updated", onBrandUpdate);
      window.removeEventListener("askredline:company-brand-updated", onBrandUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const [smsStatus, setSmsStatus] = useState(null);
  useEffect(() => {
    if (user?.role === "super_admin" || user?.role === "platform_admin") {
      api.get("/messaging/status").then((r) => setSmsStatus(r.data)).catch(() => {});
    }
  }, [user?.role]);
  const smsSuper = user?.role === "super_admin" || user?.role === "platform_admin";

  const inCommunityMode = isManager || ((isSuper || isCompany) && !!activeTenantId);
  const managerDefault = user?.tenant_id || tenants[0]?.id || null;
  const contextTenantId = isManager
    ? ((activeTenantId && tenants.some((t) => t.id === activeTenantId)) ? activeTenantId : managerDefault)
    : activeTenantId;
  // Multi-community managers get the "Mine" section; until they pick a community the sidebar
  // shows "Choose a community" and only Mine → Pulse.
  const mineSection = rawRole === "hoa_manager" && tenants.length > 1;
  const communityPicked = !mineSection || (!!activeTenantId && tenants.some((t) => t.id === activeTenantId));

  // Pulse badge (# red) + per-community bands for the switcher dots — portfolio consoles and Mine.
  const [pulseBadge, setPulseBadge] = useState({ red: 0, bands: {} });
  const pulseBadgeScope = mineSection ? "mine" : (actingCompany && !inCommunityMode) ? (portfolioId || "co") : null;
  useEffect(() => {
    if (!pulseBadgeScope) { setPulseBadge({ red: 0, bands: {} }); return; }
    let alive = true;
    const q = (isSuper && portfolioId) ? `?company_id=${portfolioId}` : "";
    const pull = () => api.get(`/pulse/badge${q}`).then((r) => { if (alive) setPulseBadge(r.data || { red: 0, bands: {} }); }).catch(() => {});
    pull();
    const timer = setInterval(pull, 300000);
    return () => { alive = false; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulseBadgeScope, location.pathname]);

  // Shared, customizable sidebar layout — scoped per console (community / portfolio / company).
  const _companyId = user?.management_company_id || (typeof viewCompany === "string" ? viewCompany : viewCompany?.id) || null;
  const scopeKey = isOperator ? null
    : inCommunityMode ? (contextTenantId ? `community:${contextTenantId}` : null)
    : actingCompany ? (_companyId ? `portfolio:${_companyId}` : null)
    : isSuper ? "company:global"
    : null;
  const canCustomize = !!scopeKey && !viewAsUser && (
    (inCommunityMode && (role === "hoa_manager" || isSuper)) ||
    (actingCompany && (role === "company_admin" || isSuper)) ||
    (!inCommunityMode && !actingCompany && isSuper)
  );

  // Redline company console badges (feature requests · feedback about the AI) — super/platform only.
  const [adminCounts, setAdminCounts] = useState({});
  useEffect(() => {
    if (inCommunityMode || !["super_admin", "platform_admin"].includes(role)) { setAdminCounts({}); return; }
    let alive = true;
    const pull = () => api.get("/admin/nav-counts").then((r) => { if (alive) setAdminCounts(r.data || {}); }).catch(() => {});
    pull();
    const timer = setInterval(pull, 60000);
    window.addEventListener("askredline:counts-changed", pull);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("askredline:counts-changed", pull); };
  }, [inCommunityMode, role, location.pathname]);

  // Live sidebar counts — ONE call for every badge (it used to be four). Refreshes on
  // navigation, every 60s, and instantly when a page broadcasts that it changed something
  // (approvals, renewals, teach-your-assistant). A module that's off comes back null → no badge.
  useEffect(() => {
    if (!inCommunityMode || !contextTenantId) { setCounts(EMPTY_COUNTS); return; }
    let alive = true;
    const pull = () => api.get(`/tenants/${contextTenantId}/nav-counts`)
      .then((r) => { if (alive) setCounts({ ...EMPTY_COUNTS, ...(r.data || {}) }); })
      .catch(() => {});
    pull();
    const timer = setInterval(pull, 60000);
    const onSug = (e) => {
      if (e.detail?.tenantId && e.detail.tenantId !== contextTenantId) return;
      // Copilot Inbox ON → module badges are rolled into Mail; a page's local count must not re-badge them.
      if (typeof e.detail?.count === "number") setCounts((c) => (c.copilot_inbox ? c : { ...c, knowledge_suggestions: e.detail.count }));
    };
    const onOverdue = (e) => {
      if (e.detail?.tenantId && e.detail.tenantId !== contextTenantId) return;
      if (typeof e.detail?.count === "number") setCounts((c) => (c.copilot_inbox ? c : { ...c, renewals_overdue: e.detail.count }));
    };
    window.addEventListener("redline:sug-count", onSug);
    window.addEventListener("redline:renewals-overdue", onOverdue);
    window.addEventListener("askredline:approvals-changed", pull);
    window.addEventListener("askredline:counts-changed", pull);
    return () => {
      alive = false; clearInterval(timer);
      window.removeEventListener("redline:sug-count", onSug);
      window.removeEventListener("redline:renewals-overdue", onOverdue);
      window.removeEventListener("askredline:approvals-changed", pull);
      window.removeEventListener("askredline:counts-changed", pull);
    };
  }, [inCommunityMode, contextTenantId, location.pathname]);

  // Emergency responder self-check: powers the "verify your mobile" banner for staff who are
  // on the on-call ladder but unreachable. Refetches on navigation so it clears after verifying.
  useEffect(() => {
    if (!inCommunityMode || !contextTenantId || !["hoa_manager", "board_member"].includes(role) || !areaAllowed("emergency")) { setEmStatus(null); return; }
    api.get(`/tenants/${contextTenantId}/emergency/my-status`)
      .then((r) => setEmStatus(r.data))
      .catch(() => setEmStatus(null));
  }, [inCommunityMode, contextTenantId, role, location.pathname]);

  // Profile-completeness nudge: community staff who are missing an email, a mobile, or mobile
  // verification silently miss the report / text & call alerts. Quiet amber banner (below), with
  // a "Fix now" jump to their account. Dismiss snoozes it for 7 days (per user, in localStorage).
  const STAFF_NUDGE_ROLES = ["hoa_manager", "board_member", "regional_manager", "company_admin"];
  const emergencyVerifyShowing = inCommunityMode && !viewAsUser && emStatus?.enabled
    && emStatus?.on_ladder && !emStatus?.phone_verified && location.pathname !== "/dashboard/account";
  const [profileNudgeSnoozed, setProfileNudgeSnoozed] = useState(true);
  useEffect(() => {
    try {
      const until = Number(localStorage.getItem(`askredline.profileNudge.snooze.${user?.user_id}`) || 0);
      setProfileNudgeSnoozed(Date.now() < until);
    } catch { setProfileNudgeSnoozed(false); }
  }, [user?.user_id]);
  const snoozeProfileNudge = () => {
    try { localStorage.setItem(`askredline.profileNudge.snooze.${user?.user_id}`, String(Date.now() + 7 * 864e5)); } catch { /* ignore */ }
    setProfileNudgeSnoozed(true);
  };
  const profileGaps = [];
  if (!viewAsUser && user?.email && user?.has_password === false) profileGaps.push("set a password so you can sign in from any device");
  if (STAFF_NUDGE_ROLES.includes(role) && !viewAsUser) {
    if (!user?.email) profileGaps.push("add your email so the report and updates reach you");
    if (!user?.phone) profileGaps.push("add your mobile number for text & call alerts");
    else if (!user?.phone_verified && !emergencyVerifyShowing) profileGaps.push("verify your mobile so text & call alerts reach you");
  }
  const showProfileNudge = profileGaps.length > 0 && !profileNudgeSnoozed && location.pathname !== "/dashboard/account";

  // Load the shared custom sidebar layout for the current console (+ which menu style it uses).
  useEffect(() => {
    if (!scopeKey) { setNavLayout(null); setNavPreset("classic"); return; }
    api.get(`/nav-layout?key=${encodeURIComponent(scopeKey)}`)
      .then((r) => {
        setNavLayout(r.data?.layout || null);
        setNavPreset(r.data?.preset === "v2" ? "v2" : "classic");
        setNavPresetSource(r.data?.preset_source || "default");
        setPlatformNavDefault(r.data?.platform_default || null);
      })
      .catch(() => { setNavLayout(null); setNavPreset("classic"); });
  }, [scopeKey]);

  // Routing guards: company vs community mode; operators are locked to the Operator Desk
  useEffect(() => {
    if (loading) return;
    if (ALWAYS_ALLOWED_PATHS.includes(location.pathname)) return;
    if (LEGACY_REDIRECT_PATHS.includes(location.pathname)) return;
    if (isOperator) {
      if (location.pathname !== "/dashboard/operator") navigate("/dashboard/operator", { replace: true });
      return;
    }
    if (mineSection && tenantsFresh && !communityPicked && !MINE_ALLOWED_PATHS.includes(location.pathname)) {
      navigate("/dashboard/mine/pulse", { replace: true });
      return;
    }
    if (isCompany) {
      if (!activeTenantId && !PORTFOLIO_PATHS.includes(location.pathname)) {
        const to = pendingPortfolioPath.current && PORTFOLIO_PATHS.includes(pendingPortfolioPath.current) ? pendingPortfolioPath.current : "/dashboard/portfolio";
        pendingPortfolioPath.current = null;
        navigate(to, { replace: true });
      }
      return;
    }
    if (!isSuper) return;
    const path = location.pathname;
    if (viewCompany) {
      if (!activeTenantId && !PORTFOLIO_PATHS.includes(path)) {
        // Entering a portfolio straight onto one of its pages (e.g. Pulse): the router commits the
        // path a tick after the state, so honour the requested destination instead of Overview.
        const to = pendingPortfolioPath.current && PORTFOLIO_PATHS.includes(pendingPortfolioPath.current) ? pendingPortfolioPath.current : "/dashboard/portfolio";
        pendingPortfolioPath.current = null;
        navigate(to, { replace: true });
      }
      return;
    }
    const inCompany = COMPANY_PATHS.includes(path);
    if (!activeTenantId && !inCompany) {
      navigate("/dashboard/communities", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isSuper, isOperator, isCompany, viewCompany, activeTenantId, location.pathname, mineSection, communityPicked, tenantsFresh]);

  const enterCommunity = (id, to = "/dashboard") => { setViewPersona(null); setActiveTenantId(id); navigate(to); };
  const exitCommunity = (to) => {
    const dest = typeof to === "string" ? to : (actingCompany ? "/dashboard/portfolio" : "/dashboard/communities");
    pendingPortfolioPath.current = dest;
    setViewPersona(null); setActiveTenantId(null);
    navigate(dest);
  };
  const enterCompanyView = (company, to = "/dashboard/portfolio") => {
    const dest = typeof to === "string" ? to : "/dashboard/portfolio";
    pendingPortfolioPath.current = dest;
    setViewCompany(company); setActiveTenantId(null); navigate(dest);
  };
  const exitCompanyView = () => { setViewCompany(null); setActiveTenantId(null); navigate("/dashboard/management-companies"); };
  // Switching the portfolio persona changes what /tenants returns — reload the list and go back to Overview.
  const choosePortfolioPersona = (p) => {
    setPortfolioPersona(p, viewCompany?.id);
    setPortfolioPickerOpen(false);
    setActiveTenantId(null);
    refreshTenants();
    navigate("/dashboard/portfolio");
  };
  // "Viewing as" from the Redline console — drop into exactly what a given seat sees.
  const chooseSeat = (pick) => {
    const seat = seatPick;
    setSeatPick(null);
    if (seat === "principal") { enterCompanyView(pick); return; }
    if (seat === "pm") { setPmCompany(pick); return; }              // step 2: which communities
    if (seat === "board") {
      setActiveTenantId(pick.id); setViewPersona("board_member"); navigate("/dashboard");
      return;
    }
  };
  const choosePmCommunities = (ids) => {
    const co = pmCompany;
    setPmCompany(null);
    if (!co) return;
    pendingPortfolioPath.current = "/dashboard/portfolio";
    setViewCompany(co);
    setPortfolioPersona({ tenant_ids: ids }, co.id);
    setActiveTenantId(null);
    refreshTenants();
    navigate("/dashboard/portfolio");
  };

  // Assignment / in-app notifications
  useEffect(() => {
    if (isOperator) return;
    let alive = true;
    const fetchN = () => api.get("/notifications").then((r) => { if (!alive) return; setNotifs(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => {});
    fetchN();
    const t = setInterval(fetchN, 30000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOperator, location.pathname]);

  const markAllRead = async () => {
    if (!unread) return;
    try { await api.post("/notifications/read", {}); } catch { /* noop */ }
    setUnread(0); setNotifs((ns) => ns.map((n) => ({ ...n, read: true })));
  };
  const markOneRead = (id) => {
    api.post("/notifications/read", { ids: [id] }).catch(() => {});
    setNotifs((ns) => ns.map((x) => (x.id === id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
  };
  const NOTIF_GROUP_LABELS = {
    pending_enrollment: `${V.lcs("person")} awaiting approval`,
    pending_reservation: "booking requests waiting",
    new_request: `new ${V.lc("person")} requests`,
    message_taken: "phone messages taken",
    new_optin: "new opt-ins",
    new_feedback: "pieces of feedback",
    escalated_conversation: "conversations waiting for a person",
    assignment: "requests assigned to you",
    ticket_finished: "requests finished",
    access_request: "portal access requests",
    vendor_application: "vendors who applied",
    vendor_ready_for_review: "vendors waiting on network approval",
    renewal_due: "renewals coming due",
    website_failing: "websites that couldn't be read",
    website_changes: "website updates found",
    usage_alert: "usage allowance alerts",
    ingest: "documents finished indexing",
    mail_new: "new mail",
    mail_auto_published: "mail published automatically",
    emergency_alert: "emergency alerts",
    emergency_drill_due: "emergency drills due",
    signature: "signature updates",
    knowledge_expiry: "knowledge going out of date",
    request_reply: "new comments on requests",
    board_request_update: "updates on your requests",
    reservation_lapsed: "bookings that lapsed with no answer",
    verify_phone_reminder: "reminders to verify your mobile",
    vendor_ready: "vendors ready to work",
    fs_proposal: "new vendor proposals",
    access_request_stale: "portal access requests waiting too long",
    pending_enrollment_stale: `${V.lcs("person")} waiting too long to be confirmed`,
    settings_change_request: "settings change requests",
  };
  // Consecutive alerts of the same kind collapse into one row, so one noisy type can't bury
  // everything else in the bell.
  const notifGroups = useMemo(() => {
    const out = [];
    for (const n of notifs) {
      const last = out[out.length - 1];
      if (last && last.type === n.type) {
        last.items.push(n);
        if (!n.read) last.unread += 1;
      } else {
        out.push({ key: n.id, type: n.type, items: [n], unread: n.read ? 0 : 1,
                   label: NOTIF_GROUP_LABELS[n.type] || "notifications" });
      }
    }
    return out;
  }, [notifs]);

  const goToNotif = (n) => {
    if (!n.read) {
      api.post("/notifications/read", { ids: [n.id] }).catch(() => {});
      setNotifs((ns) => ns.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    // A bell item from another community (a portfolio admin, a manager running several) opens IN
    // that community — before iter479e only super admins were switched, everyone else landed on
    // the wrong community's page (iter479d's blank-panel bug, via the bell).
    if (n.tenant_id && n.tenant_id !== contextTenantId && tenants.some((t) => t.id === n.tenant_id)) {
      setViewPersona(null); setActiveTenantId(n.tenant_id);
    }
    // iter256 — some emitters stored an ABSOLUTE url (https://host/dashboard/...). Feeding that
    // to react-router navigated to a bogus "/https://..." route, so those bell items went
    // nowhere. Normalise to a path, then fall back on the ids the notification carries instead
    // of dumping everyone on the Requests page.
    if (n.link) {
      try {
        const u = new URL(n.link, window.location.origin);
        navigate(u.pathname + u.search);
      } catch { navigate(n.link); }
      return;
    }
    if (n.action_id) { navigate(`/dashboard/actions?ticket=${n.action_id}`); return; }
    if (n.conversation_id) { navigate(`/dashboard/conversations?open=${n.conversation_id}`); return; }
    navigate({ pending_enrollment: "/dashboard/roster?filter=pending",
               new_optin: "/dashboard/roster?consent=optins&range=week",
               new_feedback: "/dashboard/feedback",
               renewal_due: "/dashboard/renewals",
               access_request: "/dashboard/public-page",
               settings_change_request: "/dashboard/settings" }[n.type] || "/dashboard/actions");
  };

  // Notification preferences are set up via the user menu on demand. We do NOT
  // auto-open the onboarding modal — pushing a 6-toggle configuration screen at
  // a board member the very first time they log in was intrusive AND the modal
  // did not fit small mobile viewports. If a hint is ever needed, add a soft
  // banner or empty-state nudge, not a full-screen dialog.

  // Sidebar community switcher: open state for the Command palette
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const tenant = tenants.find((t) => t.id === contextTenantId);
  // Industry words for every page rendered below (V from lib/vocab); remount pages when the pack changes.
  // Outside a community (portfolio console: Communities, Team, Roles, Pulse…) there is no selected
  // tenant, which used to leave V on the HOA fallback — so a CPA firm's own Team page offered to
  // invite a "Community Manager" (iter508). When every community in view runs the same industry pack,
  // speak that pack; only a genuinely mixed portfolio keeps the neutral HOA words.
  const packTenant = useMemo(() => {
    // A super admin sees every tenant, so scope to the portfolio being viewed — otherwise the whole
    // platform reads as "mixed" and a CPA firm's own Team page falls back to the HOA words.
    const pool = portfolioId ? tenants.filter((t) => t.company_id === portfolioId) : tenants;
    const ids = [...new Set(pool.map((t) => t.industry_pack?.id).filter(Boolean))];
    return ids.length === 1 ? pool.find((t) => t.industry_pack?.id === ids[0]) : null;
  }, [tenants, portfolioId]);
  const vocabKey = useMemo(() => {
    const src = tenant || packTenant;
    setActiveVocab(src);
    return JSON.stringify([src?.industry || "", src?.vocab_overrides || null, !!tenant]);
  }, [tenant, packTenant]);

  // "View as": super admin previews the community exactly as a community user sees it
  const canViewAsUser = isSuper && inCommunityMode;
  // Company admins / regionals get full manager powers inside a community (drill-in),
  // so community pages treat them like an HOA manager.
  const portfolioPreview = isSuper && !!viewCompany && !!portfolioPersona;
  const effRole = (canViewAsUser && viewPersona) ? viewPersona
    : ((isCompany || portfolioPreview) && inCommunityMode) ? "hoa_manager" : portfolioPreview ? "regional_manager" : role;
  const effIsSuper = ["super_admin", "platform_admin", "onboarding_admin"].includes(effRole);
  const featureOn = (f) => !f || tenant?.features?.[f] !== false;
  // Visibility of a nav item for a GIVEN role — used for the live nav (effRole) and for
  // the view-as pill to decide whether the current page survives a persona switch.
  const visForRole = (n, r) => {
    const rIsSuper = ["super_admin", "platform_admin", "onboarding_admin"].includes(r);
    if (n.companyHide && isCompany) return false;
    if (n.superAlways && rIsSuper) return true; // e.g. Settings: supers see it even when the community's agent_config feature is off
    if (n.superOnly && !rIsSuper) return false;
    // `boardToo` — iter330. The backend has let a board member invite their own community's
    // managers and fellow board members since iter327, but the nav link was manager-only, so
    // the page was only reachable by guessing the URL.
    if (n.managerUp && !(rIsSuper || r === "hoa_manager" || (n.boardToo && r === "board_member"))) return false;
    if (n.requiresConfig && r === "board_member" && !user?.can_configure) return false;
    if (n.boardGrant && r === "board_member" && !user?.[n.boardGrant]) return false;
    // Mail settings → "Board members in Mail" = Hidden: the page isn't offered to board seats at all.
    if (n.mailGate && r === "board_member" && tenant?.mail?.board_access === "none") return false;
    if (n.modeGate && (tenant?.mode || "answers_requests") !== n.modeGate) return false;
    // Copilot is a PREMIUM per-community entitlement. The Ask Copilot toggle in Settings →
    // Capabilities hides it for EVERYONE, super admins included (iter408 — the old super-admin
    // bypass made the toggle look broken). Per-audience access still only applies to non-supers.
    if (n.copilotGate) {
      const rSuperish = ["super_admin", "platform_admin"].includes(r);
      if (!tenant?.copilot_enabled) return false;
      // Per-audience access (managers / board), set in Settings → Capabilities & Actions → Ask Copilot.
      if (!rSuperish) {
        const acc = tenant?.copilot_access || {};
        if (["hoa_manager", "company_admin", "regional_manager"].includes(r) && acc.managers === false) return false;
        if (r === "board_member" && acc.board === false) return false;
      }
    }
    if (n.entGate === "broadcasts" && !(tenant?.entitlements?.broadcasts && tenant?.broadcasts_enabled)) return false;
    if (n.entGate === "amenities" && !(tenant?.entitlements?.amenities && tenant?.amenities_enabled)) return false;
    if (n.entGate === "surveys" && !(tenant?.entitlements?.surveys && tenant?.surveys_enabled)) return false;
    if (n.entGate === "signatures" && !(tenant?.entitlements?.signatures && tenant?.signatures_enabled)) return false;
    if (n.entGate === "emergency_escalation" && !(tenant?.entitlements?.emergency_escalation && tenant?.emergency_escalation_enabled)) return false;
    if (n.entGate === "field_services" && !(tenant?.entitlements?.field_services && tenant?.field_services_enabled)) return false;
    // Checklists is its own module (iter527) — its own switch, no Field Services dependency.
    if (n.to === "/dashboard/checklists" && !tenant?.checklists?.enabled) return false;
    // Pulse is a management tool — never offered to board seats, and only where the add-on is on.
    if (n.entGate === "pulse" && !(tenant?.entitlements?.pulse && tenant?.pulse_enabled)) return false;
    if (n.noBoard && r === "board_member") return false;
    return featureOn(n.feature);
  };
  // Switching personas stays ON the current page when that persona can see it — no more
  // losing your place. Only jumps to Overview when the page wouldn't exist for that role.
  const enableViewAs = (persona) => {
    setViewPersona(persona);
    const reachable = [...COMMUNITY_NAV, ...COMMUNITY_TOOLS].filter((n) => visForRole(n, persona));
    const ok = reachable.some((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));
    if (!ok) navigate("/dashboard");
  };
  const PERSONA_LABELS = { hoa_manager: "Community Manager", board_member: "Board member" };

  // "View as resident" — pick a roster person, mint a portal session, open the portal.
  const [residentPickOpen, setResidentPickOpen] = useState(false);
  const [pickHouseholds, setPickHouseholds] = useState(null);
  const [pickQ, setPickQ] = useState("");
  const [impersonating, setImpersonating] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const openResidentPicker = () => {
    setResidentPickOpen(true); setPickQ(""); setPickHouseholds(null);
    api.get(`/tenants/${contextTenantId}/households`)
      .then((r) => setPickHouseholds(r.data || []))
      .catch(() => setPickHouseholds([]));
  };
  const impersonateResident = async (person) => {
    setImpersonating(true);
    try {
      const r = await api.post(`/tenants/${contextTenantId}/impersonate-resident`, { person_id: person.id });
      // The portal session rides the HttpOnly resident_token cookie the server just set (iter475).
      setResidentPickOpen(false);
      toast.success(`Opening the portal as ${r.data.name || `${V.lc("person")}`}…`);
      window.open("/portal", "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || `Couldn't open the portal as this ${V.lc("person")}`);
    } finally { setImpersonating(false); }
  };

  const managerMulti = isManager && tenants.length > 1;
  // What the switcher may show. A "view as" persona is one person at one community; a super admin inside a
  // management company's portfolio sees that portfolio only. Never the whole platform from inside a preview.
  const switchableTenants = viewAsUser ? tenants.filter((t) => t.id === contextTenantId)
    : (isSuper && viewCompany) ? tenants.filter((t) => t.company_id === (typeof viewCompany === "string" ? viewCompany : viewCompany?.id))
    : tenants;
  const canSwitchTenant = !viewAsUser && (isSuper || isCompany || managerMulti) && switchableTenants.length > 1;
  const showExitToPortfolio = (isSuper || isCompany) && inCommunityMode && !viewAsUser;
  const exitLabel = actingCompany ? `Back to ${portfolioName && portfolioName.length <= 22 ? portfolioName : "Portfolio"}` : (isCompany ? "Back to Company" : `Exit to all ${V.lcs("org")}`);

  let navItems = [];
  let toolItems = [];
  const v2 = inCommunityMode && navPreset === "v2";
  if (isOperator) {
    navItems = [{ to: "/dashboard/operator", icon: Headset, label: "Operator Desk" }];
  } else if (inCommunityMode) {
    const vis = (n) => visForRole(n, effRole) && areaAllowed(n.area);
    navItems = COMMUNITY_NAV.filter(vis);
    toolItems = COMMUNITY_TOOLS.filter(vis);
    // v2 folds Live Demo into Settings — no "Tools" section — and orders items within a section.
    if (v2) { navItems = [...navItems, ...toolItems].sort((a, b) => (a.order2 ?? 99) - (b.order2 ?? 99)); toolItems = []; }
  } else if (actingCompany) {
    const canTeam = role === "company_admin" || (isSuper && !!viewCompany && !portfolioPreview);
    // Module toggles roll up: a portfolio nav item shows only when at least ONE of its
    // communities has that module on (Requests module / Field Services add-on).
    const anyRequests = switchableTenants.some((t) => t.features?.requests !== false);
    const anyFS = switchableTenants.some((t) => t.entitlements?.field_services && t.field_services_enabled);
    const anyPulse = switchableTenants.some((t) => t.entitlements?.pulse && t.pulse_enabled);
    // Team is for every company seat (regionals read-only); Settings stays administrator-only.
    navItems = portfolioNavFactory().filter((n) => !(n.companyAdminOnly && !canTeam)
      && !(n.anyFeature === "requests" && !anyRequests)
      && !(n.anyFieldServices && !anyFS)
      && !(n.anyPulse && !anyPulse));
  } else {
    navItems = COMPANY_NAV.filter((n) => {
      if (OWNER_ONLY_PATHS.includes(n.to)) return isOwner;
      if (role === "onboarding_admin" && ONBOARDING_HIDDEN_PATHS.includes(n.to)) return false;
      return true;
    });
  }
  // Label precedence: the community's own rename (settings) → its industry pack's module name
  // (non-HOA packs only, so HOA menus are unchanged) → the v2 short label → the default.
  const packLabel = (n) => (n.vkey && tenant?.industry_pack && tenant.industry_pack.is_default === false && tenant?.vocab?.modules?.[n.vkey]) || "";
  const navLabel = (n) => (n.labelKey && (tenant?.[n.labelKey] || "").trim()) || packLabel(n) || (v2 && n.label2) || n.label;
  // Badge value for an item: a number (0 = hidden), true for the emergency dot, or null.
  const badgeOf = (n) => {
    if (n.pulseBadge) return pulseBadge.red > 0 ? pulseBadge.red : null;
    if (n.adminCountKey && !inCommunityMode) {
      const a = adminCounts[n.adminCountKey];
      return a ? a : null;
    }
    if (!inCommunityMode || !n.countKey) return null;
    const v = counts[n.countKey];
    if (v == null || v === false || v === 0) return null;
    return v;
  };

  // iter337 — inside a community the console wears THAT community's identity: their logo and
  // name at the top of the sidebar, their colour on the selected nav item, their font
  // throughout. `brand` is resolved by the backend (`branding.console_brand`) so the font
  // catalog stays in one place. Outside a community it's the Redline console as before.
  // Outside a community the console wears the PLATFORM look (super admin → Appearance).
  const [platformBrand, setPlatformBrand] = useState(null);
  useEffect(() => {
    let alive = true;
    const pull = () => api.get("/platform/appearance").then((r) => { if (alive) setPlatformBrand(r.data); }).catch(() => {});
    pull();
    const onChange = (e) => setPlatformBrand((b) => ({ ...(b || {}), ...(e.detail || {}) }));
    window.addEventListener("platform-appearance-changed", onChange);
    return () => { alive = false; window.removeEventListener("platform-appearance-changed", onChange); };
  }, []);
  const platformLook = platformBrand && (platformBrand.font !== "default" || platformBrand.color) ? platformBrand : null;
  // Portfolio console: the firm's colour/font/logo, falling back to the platform look per field.
  const portfolioLook = actingCompany && portfolio
    ? { ...(platformLook || {}), ...portfolio, color: portfolio.color || platformLook?.color || "" }
    : null;
  const brand = inCommunityMode ? tenant?.brand : (portfolioLook || platformLook);
  const navSel = brand?.color ? navBrand(brand.color) : null;
  // Every red accent in the console (buttons, badges, focus rings, charts, dialogs) follows the
  // community colour while inside a community; the platform accent outside.
  useBrandTheme(brand?.color);
  const logoUrl = brand?.logo && tenant?.id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/tenant/${tenant.id}/logo`
    : "";
  const portfolioLogoUrl = portfolio?.logo && portfolio?.id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/company/${portfolio.id}/logo?v=${portfolio.logo_ver || ""}`
    : "";
  const [portfolioLogoBroken, setPortfolioLogoBroken] = useState(false);
  useEffect(() => { setPortfolioLogoBroken(false); }, [portfolioLogoUrl]);
  const portfolioMark = portfolioLogoUrl && !portfolioLogoBroken ? (
    <img src={portfolioLogoUrl} alt="" onError={() => setPortfolioLogoBroken(true)} data-testid="sidebar-portfolio-logo"
      className="h-9 w-9 shrink-0 rounded-xl bg-white/95 object-contain p-0.5" />
  ) : (
    <div data-testid="sidebar-portfolio-mark"
      className="h-9 w-9 shrink-0 rounded-xl flex items-center justify-center font-heading font-extrabold text-sm"
      style={{ backgroundColor: navSel?.bg || "#c1121f", color: navSel?.fg || "#ffffff" }}>
      {((portfolioName || "P").trim()[0] || "P").toUpperCase()}
    </div>
  );

  // Two consoles, two chromes (iter448/iter452): the FIRM's Light/Dark choice paints its portfolio
  // console AND all its communities; a community can override itself (resolved server-side, community
  // override → firm → light, into tenant.brand.sidebar_theme). The portfolio console reads the firm
  // default from the company brand (portfolio.sidebar_theme). Redline & the operator desk stay dark.
  const communityTheme = tenant?.brand?.sidebar_theme === "dark" ? "dark" : "light";
  const firmTheme = portfolio?.sidebar_theme === "dark" ? "dark" : "light";
  const resolvedTheme = inCommunityMode ? communityTheme : (actingCompany && portfolio ? firmTheme : "dark");
  const lightShell = !isOperator && resolvedTheme === "light";
  const S = lightShell ? {
    aside: "bg-white text-ink border-r border-sand-200", line: "border-sand-200",
    name: "text-ink", sub: "text-ink/50", dim: "text-ink/60", link: "text-ink/65 hover:text-ink",
    label: "text-ink/45 hover:text-ink", idle: "text-ink/70 hover:bg-sand-100 hover:text-ink",
    active: "bg-redline-500 text-white", hover: "hover:bg-sand-100", dashed: "border-ink/30",
    chip: "bg-ink/5 text-ink/60", chipStrong: "bg-ink/10 text-ink", grey: "bg-ink/10 text-ink",
  } : {
    aside: "bg-ink text-sand-100", line: "border-white/10",
    name: "text-white", sub: "text-sand-400", dim: "text-sand-300", link: "text-sand-300 hover:text-white",
    label: "text-sand-400 hover:text-white", idle: "text-sand-300 hover:bg-white/5 hover:text-white",
    active: "bg-redline-500 text-white", hover: "hover:bg-white/5", dashed: "border-white/30",
    chip: "bg-white/10 text-sand-300", chipStrong: "bg-white/15 text-sand-100", grey: "bg-white/15 text-sand-100",
  };

  const communityMark = logoUrl && !logoBroken ? (
    <img src={logoUrl} alt="" onError={() => setLogoBroken(true)} data-testid="sidebar-community-logo"
      className="h-9 w-9 shrink-0 rounded-xl bg-white/95 object-contain p-0.5" />
  ) : (
    <div data-testid="sidebar-community-mark"
      className="h-9 w-9 shrink-0 rounded-xl flex items-center justify-center font-heading font-extrabold text-sm"
      style={{ backgroundColor: navSel?.bg || "#c1121f", color: navSel?.fg || "#ffffff" }}>
      {((tenant?.name || "?").trim()[0] || "?").toUpperCase()}
    </div>
  );

  const communityName = (
    <span className="sidebar-label min-w-0 flex-1 leading-tight">
      <span className={`block font-heading font-extrabold ${S.name} text-sm line-clamp-2`}
        title={tenant?.name} data-testid="sidebar-community-name">
        {tenant?.name}
      </span>
      <span className={`block text-[9px] uppercase tracking-[0.1em] ${S.sub} break-words`}><span className={`font-bold ${S.dim}`}>{V.one("org")}</span> · Powered by Ask Redline</span>
    </span>
  );

  const NavItem = ({ n }) => {
    const b = badgeOf(n);
    return (
    <SidebarHint collapsed={sidebarCollapsed} label={`${navLabel(n)}${b === true ? ' — active emergency' : typeof b === 'number' ? ` — ${b} ${n.countTitle || 'notifications'}` : ''}`} testId={`nav-hint-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
    <NavLink to={n.to} end={n.end} aria-label={navLabel(n)} onClick={() => setSidebarOpen(false)} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
      style={navSel ? { "--sidebar-active-bg": navSel.bg, "--sidebar-active-fg": navSel.fg } : undefined}
      className={`sidebar-nav-item flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${S.idle}`}>
      <n.icon className="h-4.5 w-4.5 shrink-0" strokeWidth={1.5} />
      <span className="sidebar-label flex-1 flex items-center gap-1.5">
        {navLabel(n)}
        {n.copilotTag && counts.copilot_inbox === true && (
          <span data-testid="nav-copilot-tag" title="Copilot Inbox is on for this community"
            className={`shrink-0 text-[9.5px] font-semibold tracking-wide px-1.5 py-0.5 rounded-full ${S.chip}`}>copilot</span>
        )}
      </span>
      {b === true && (
        <span data-testid={n.badgeId} title={n.countTitle} className="sidebar-nav-dot relative flex h-2.5 w-2.5 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
        </span>
      )}
      {typeof b === "number" && (
        <span data-testid={n.badgeId} title={n.countTitle}
          className={`sidebar-nav-badge min-w-5 h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${(n.tone && n.tone !== "grey" && BADGE_TONE[n.tone]) || S.grey}`}>
          {b > 99 ? "99+" : b}
        </span>
      )}
    </NavLink>
    </SidebarHint>
    );
  };

  // iter368 — header search / ⌘K is opt-in per console (Organize menu), default off.
  const quickSearchOn = !!navLayout?.quick_search;
  const groupOf = v2 ? (n) => n.group2 || n.group : (n) => n.group;
  const activeGroups = isOperator ? null : inCommunityMode ? (v2 ? COMMUNITY_GROUPS_V2 : COMMUNITY_GROUPS) : actingCompany ? PORTFOLIO_GROUPS : COMPANY_GROUPS;
  // A saved arrangement only applies to the menu style it was built for.
  const layoutForPreset = navLayout && ((navLayout.preset || "classic") === navPreset || !inCommunityMode) ? navLayout : null;
  const arrangement = activeGroups ? buildArrangement(navItems, activeGroups, layoutForPreset, groupOf) : null;
  const defaultArrangement = activeGroups ? buildArrangement(navItems, activeGroups, null, groupOf) : null;
  const defaultArrangementFor = (preset) => {
    if (!inCommunityMode) return defaultArrangement;
    const items = preset === "v2"
      ? [...COMMUNITY_NAV, ...COMMUNITY_TOOLS].filter((n) => visForRole(n, effRole)).sort((a, b) => (a.order2 ?? 99) - (b.order2 ?? 99))
      : COMMUNITY_NAV.filter((n) => visForRole(n, effRole));
    return buildArrangement(items, preset === "v2" ? COMMUNITY_GROUPS_V2 : COMMUNITY_GROUPS, null, preset === "v2" ? (n) => n.group2 || n.group : (n) => n.group);
  };
  const navGroups = activeGroups
    ? activeGroups.map((g) => ({ g, items: arrangement[g] })).filter((x) => x.items.length)
    : null;
  // Roll-up for a collapsed section: the sum of its numeric badges, plus the emergency dot.
  const rollupOf = (items) => items.reduce((acc, n) => {
    const b = badgeOf(n);
    if (b === true) acc.dot = true; else if (typeof b === "number") acc.n += b;
    return acc;
  }, { n: 0, dot: false });

  const saveNavLayout = async (layout) => {
    setSavingLayout(true);
    try {
      const r = await api.put("/nav-layout", { key: scopeKey, layout });
      setNavLayout(r.data?.layout || layout);
      if (r.data?.preset) { setNavPreset(r.data.preset); setNavPresetSource(r.data.preset_source || "community"); }
      setCustomizeOpen(false);
      toast.success("Menu updated for this console");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the menu");
    } finally {
      setSavingLayout(false);
    }
  };
  const savePlatformNavDefault = async (preset) => {
    try {
      const r = await api.put("/nav-default", { preset });
      setPlatformNavDefault(r.data?.preset || null);
      toast.success(preset ? `Platform default set to the ${preset === "v2" ? "new" : "classic"} menu` : "Platform default cleared");
      const l = await api.get(`/nav-layout?key=${encodeURIComponent(scopeKey)}`);
      setNavPreset(l.data?.preset === "v2" ? "v2" : "classic"); setNavPresetSource(l.data?.preset_source || "default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the platform default");
    }
  };

  // Collapsible nav sections — persisted so the sidebar stays the way you left it.
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("askredline.navCollapsed") || "{}"); } catch { return {}; }
  });
  const toggleGroup = (g) => setCollapsedGroups((c) => {
    const next = { ...c, [g]: !c[g] };
    try { localStorage.setItem("askredline.navCollapsed", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  // Never hide the page you're on silently: an explicit collapse always wins, but
  // navigating INTO a collapsed group's page auto-expands it (see effect below).
  const groupHasActive = (items) => items.some((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));
  useEffect(() => {
    if (loading || !navGroups) return;
    const active = navGroups.find(({ items }) => groupHasActive(items));
    if (active && collapsedGroups[active.g]) toggleGroup(active.g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, loading]);

  // Custom-role landing: keep them on a page their role can open. If the current community
  // page isn't in their (already area-filtered) menu, send them to the first one that is.
  const blockedCommunityPage = (() => {
    if (!isCustom || !inCommunityMode || !communityPicked) return false;
    const hit = COMMUNITY_NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));
    return !!hit && !areaAllowed(hit.area);
  })();
  useEffect(() => {
    if (loading || !isCustom || !inCommunityMode || !communityPicked) return;
    const onAllowed = navItems.some((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));
    if (!onAllowed && navItems.length) navigate(navItems[0].to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, loading, isCustom, inCommunityMode, communityPicked, contextTenantId]);

  return (
    <div className={`min-h-screen flex bg-sand-50 font-body`} {...(brand ? { "data-brandfont": "1" } : {})}>
      {brand && <BrandFont brand={brand} />}
      {sidebarOpen && (
        <div data-testid="sidebar-backdrop" onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-ink/50 z-40 lg:hidden" />
      )}

      <aside id="dashboard-sidebar" data-testid="dashboard-sidebar" data-collapsed={sidebarCollapsed} data-shell={lightShell ? "community" : "dark"} className={`app-sidebar w-64 shrink-0 ${S.aside} flex flex-col fixed h-screen z-50 transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        {/* Identity — a community's own logo, name and colour when you're inside one; the
            Redline console mark when you're not (iter337). */}
        {inCommunityMode && tenant ? (
          <div className={`sidebar-identity p-4 border-b ${S.line} flex items-start gap-2`}>
            <div className="min-w-0 flex-1 space-y-2.5">
              {showExitToPortfolio ? (
                <button data-testid="back-to-company" onClick={exitCommunity} aria-label={exitLabel} title={exitLabel}
                  className={`sidebar-switcher flex items-center gap-1.5 text-xs font-semibold ${S.link} transition-colors ${sidebarCollapsed ? "w-full" : ""}`}>
                  <ArrowLeft className="h-3.5 w-3.5" /> <span className="sidebar-label">{exitLabel}</span>
                </button>
              ) : (communityPicked && tenant?.company_name && !isOperator) ? (
                <span data-testid="sidebar-part-of-portfolio" title={`Part of the ${tenant.company_name} portfolio`}
                  className={`sidebar-label flex items-center gap-1.5 text-[11px] font-semibold ${S.dim} truncate`}>
                  <Landmark className="h-3 w-3 shrink-0" strokeWidth={2} /> <span className="truncate">{tenant.company_name}</span> <ChevronRight className="h-3 w-3 shrink-0 opacity-60" />
                </span>
              ) : null}
              {canSwitchTenant ? (
                <Popover open={switcherOpen} onOpenChange={setSwitcherOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="tenant-selector" aria-label={`Switch ${V.lc("org")}: ${tenant?.name || "Choose a community"}`} title={`Switch ${V.lc("org")}: ${tenant?.name || "Choose a community"}`}
                      className={`sidebar-switcher w-full -mx-1.5 flex items-center gap-2.5 rounded-xl px-1.5 py-1.5 text-left ${S.hover} transition-colors`}>
                      {communityPicked ? communityMark : (
                        <div className={`h-9 w-9 shrink-0 rounded-xl border border-dashed ${S.dashed} flex items-center justify-center`}><Building2 className={`h-4 w-4 ${S.dim}`} strokeWidth={1.5} /></div>
                      )}
                      {communityPicked ? communityName : (
                        <span className="sidebar-label min-w-0 flex-1 leading-tight" data-testid="choose-community-label">
                          <span className={`block font-heading font-extrabold ${S.name} text-sm`}>Choose a community</span>
                          <span className={`block text-[9px] uppercase tracking-[0.1em] ${S.sub} whitespace-nowrap`}>{switchableTenants.length} assigned to you</span>
                        </span>
                      )}
                      <ChevronDown className={`sidebar-label h-4 w-4 ${S.dim} shrink-0`} strokeWidth={1.5} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-72 p-0" data-testid="tenant-switcher-popover">
                    <Command>
                      <CommandInput data-testid="tenant-switcher-search" placeholder={`Search ${switchableTenants.length} ${V.lcs("org")}…`} />
                      <CommandList className="max-h-72">
                        <CommandEmpty>No {V.lc("org")} matches.</CommandEmpty>
                        <CommandGroup heading={actingCompany && portfolioName ? portfolioName : undefined} data-testid="tenant-switcher-group">
                          {switchableTenants.map((t) => (
                            <CommandItem
                              key={t.id}
                              value={`${t.name} ${t.phone_number || ""}`}
                              data-testid={`tenant-option-${t.id}`}
                              onSelect={() => { setViewPersona(null); setActiveTenantId(t.id); setSwitcherOpen(false); if (!communityPicked) navigate("/dashboard"); }}
                            >
                              {pulseBadge.bands?.[t.id] && <PulseDot band={pulseBadge.bands[t.id]} quiet size={8} testid={`tenant-option-pulse-${t.id}`} />}
                              <span className="flex-1 truncate">{t.name}</span>
                              {t.id === contextTenantId && communityPicked && <Check className="h-4 w-4 text-redline-500 shrink-0" strokeWidth={2} />}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="sidebar-switcher flex items-center gap-2.5" title={tenant?.name}>
                  {communityMark}
                  {communityName}
                </div>
              )}
            </div>
            <button data-testid="sidebar-close" onClick={() => setSidebarOpen(false)} className={`lg:hidden ${S.link}`}>
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        ) : actingCompany && portfolioName && !isOperator ? (
          <div className={`sidebar-identity p-4 border-b ${S.line} flex items-center gap-2.5`} data-testid="sidebar-portfolio-identity" title={portfolioName}>
            {portfolioMark}
            <span className="sidebar-label min-w-0 flex-1 leading-tight">
              <span className={`inline-flex items-center gap-1 rounded-md ${S.chip} px-1.5 py-px text-[9px] uppercase tracking-[0.18em] font-bold mb-1`} data-testid="sidebar-portfolio-eyebrow">
                <Landmark className="h-2.5 w-2.5" strokeWidth={2} /> Portfolio
              </span>
              <span className={`block font-heading font-extrabold ${S.name} text-sm line-clamp-2`} title={portfolioName} data-testid="sidebar-portfolio-name">{portfolioName}</span>
              <span className={`block text-[9px] uppercase tracking-[0.1em] ${S.sub} whitespace-nowrap`}>Powered by Ask Redline</span>
            </span>
            <button data-testid="sidebar-close" onClick={() => setSidebarOpen(false)} className={`lg:hidden ${S.link}`}>
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        ) : (
          <div className={`sidebar-identity p-5 flex items-center gap-2 border-b ${S.line}`} title="Ask Redline">
            <div className="h-9 w-9 shrink-0 rounded-xl bg-redline-500 flex items-center justify-center">
              <MessageSquareText className="h-5 w-5 text-white" strokeWidth={1.5} />
            </div>
            <div className="sidebar-label leading-tight flex-1">
              <div className="font-heading font-extrabold text-white text-sm">Ask Redline</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-sand-300">
                {isOperator ? "Operator Console" : actingCompany ? "Portfolio Console" : "Company Console"}
              </div>
            </div>
            <button data-testid="sidebar-close" onClick={() => setSidebarOpen(false)} className={`lg:hidden ${S.link}`}>
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        )}

        <nav data-testid="dashboard-navigation" aria-label="Main navigation" className="sidebar-nav flex-1 min-h-0 p-3 space-y-1 overflow-y-auto sidebar-scroll">
          {mineSection && !viewAsUser && (
            <div className={`space-y-1 pb-3 mb-3 border-b ${S.line}`} data-testid="nav-group-mine">
              <div className={`sidebar-label px-3 pb-1.5 text-[10px] uppercase tracking-[0.18em] font-bold ${S.sub}`}>Mine</div>
              {MINE_NAV.map((n) => <NavItem key={n.to} n={n} />)}
            </div>
          )}
          {!communityPicked ? (
            <div className={`sidebar-label px-3 py-2 text-xs ${S.sub}`} data-testid="nav-pick-community-hint">Pick a community above to open its menu.</div>
          ) : navGroups
            ? navGroups.map(({ g, items }, gi) => {
                const headerless = g === "Pinned";
                const collapsed = !sidebarCollapsed && !headerless && !!collapsedGroups[g];
                const hasActive = groupHasActive(items);
                const roll = collapsed ? rollupOf(items) : null;
                return (
                  <div key={g || "top"} className={gi > 0 ? `pt-4 mt-3 border-t ${S.line} space-y-1` : "space-y-1"}>
                    {g && !headerless && (
                      <button type="button" data-testid={`nav-group-${g.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => toggleGroup(g)} aria-expanded={!collapsed}
                        title={v2 ? NAV_GROUP_DESCRIPTIONS[g] : undefined}
                        className={`sidebar-label w-full flex items-center justify-between px-3 pb-1.5 text-[10px] uppercase tracking-[0.18em] font-bold ${S.label} transition-colors group/nav`}>
                        <span className="flex items-center gap-1.5">
                          {g === "Community" ? V.one("org") : g === "Community tools" ? `${V.one("org")} tools` : g}
                          {collapsed && hasActive && <span className="h-1.5 w-1.5 rounded-full bg-redline-500" title="Current page is in this section" />}
                        </span>
                        <span className="flex items-center gap-1">
                          {collapsed && roll && (roll.n > 0 || roll.dot) && (
                            <span data-testid={`nav-group-rollup-${g.toLowerCase().replace(/\s+/g, "-")}`} title="Waiting in this section"
                              className={`inline-flex items-center gap-1 rounded-full ${S.chipStrong} px-1.5 py-px text-[9px] font-bold normal-case tracking-normal`}>
                              {roll.n > 0 && (roll.n > 99 ? "99+" : roll.n)}
                              {roll.dot && <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />}
                            </span>
                          )}
                          {collapsed && !(roll && (roll.n > 0 || roll.dot)) && <span className={`rounded-full ${S.chip} px-1.5 py-px text-[9px] font-bold normal-case tracking-normal`}>{items.length}</span>}
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`} strokeWidth={2} />
                        </span>
                      </button>
                    )}
                    {!collapsed && items.map((n) => <NavItem key={n.to} n={n} />)}
                  </div>
                );
              })
            : navItems.map((n) => <NavItem key={n.to} n={n} />)}
          {communityPicked && toolItems.length > 0 && (() => {
            const collapsed = !sidebarCollapsed && !!collapsedGroups["Tools"];
            const hasActive = groupHasActive(toolItems);
            return (
              <div className={`pt-4 mt-3 border-t ${S.line} space-y-1`}>
                <button type="button" data-testid="nav-group-tools"
                  onClick={() => toggleGroup("Tools")} aria-expanded={!collapsed}
                  className={`sidebar-label w-full flex items-center justify-between px-3 pb-1.5 text-[10px] uppercase tracking-[0.18em] font-bold ${S.label} transition-colors group/nav`}>
                  <span className="flex items-center gap-1.5">
                    Tools
                    {collapsed && hasActive && <span className="h-1.5 w-1.5 rounded-full bg-redline-500" title="Current page is in this section" />}
                  </span>
                  <span className="flex items-center gap-1">
                    {collapsed && <span className={`rounded-full ${S.chip} px-1.5 py-px text-[9px] font-bold normal-case tracking-normal`}>{toolItems.length}</span>}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`} strokeWidth={2} />
                  </span>
                </button>
                {!collapsed && toolItems.map((n) => <NavItem key={n.to} n={n} />)}
              </div>
            );
          })()}
        </nav>

        <div className={`sidebar-footer p-3 border-t ${S.line} space-y-1`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" aria-label={`Account menu: ${user?.name || "Your account"}`} title={`Account menu: ${user?.name || "Your account"}`} className={`sidebar-user w-full flex items-center gap-3 px-2 py-2 rounded-xl ${S.hover} transition-colors`}>
                <div className="h-8 w-8 rounded-full bg-redline-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {(user?.name || "?")[0]}
                </div>
                <div className="sidebar-label flex-1 min-w-0 text-left">
                  <div className={`text-sm ${S.name} font-semibold truncate`}>{user?.name}</div>
                  <div className={`text-[11px] ${S.sub}`}>{isCustom ? (user?.custom_role?.name || "Custom role") : (user?.seat_label || ROLE_LABELS[role] || role)}</div>
                </div>
                <ChevronDown className={`sidebar-label h-4 w-4 ${S.dim} shrink-0`} strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56 mb-1">
              {!isOperator && (
                <DropdownMenuItem data-testid="menu-account-settings" onClick={() => navigate("/dashboard/account")}>
                  <Settings className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> Account settings
                </DropdownMenuItem>
              )}
              {inCommunityMode && (
                <DropdownMenuItem data-testid="menu-replay-tour" onClick={() => setReplayTour(Date.now())}>
                  <Sparkles className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> Replay onboarding tour
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="logout-btn" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div data-testid="dashboard-content" data-sidebar-collapsed={sidebarCollapsed} className={`sidebar-content flex-1 lg:ml-64 min-w-0 w-0 ${viewAsUser ? "ring-2 ring-inset ring-amber-400/60" : ""}`}>
        <header className="h-16 bg-sand-50/95 backdrop-blur border-b border-sand-200 sticky top-0 z-30 flex items-center justify-between px-4 lg:px-8 gap-3">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <SidebarCollapseToggle collapsed={sidebarCollapsed} onToggle={toggleSidebarCollapsed} sidebarId="dashboard-sidebar" testId="sidebar-collapse-toggle" />
            <button data-testid="sidebar-toggle" onClick={() => setSidebarOpen(true)} className="lg:hidden text-ink shrink-0 -ml-1 p-1">
              <Menu className="h-6 w-6" strokeWidth={1.5} />
            </button>
            {/* Phones: the name never fits next to five buttons, so show the community's mark
                (logo or brand-colour initial) instead of a chopped "CAS…". Tapping it opens the
                drawer, where the full name lives. Desktop keeps the text crumb. */}
            {inCommunityMode && !isOperator && (
              <button type="button" data-testid="header-community-mark" onClick={() => setSidebarOpen(true)} title={tenant?.name || `${V.one("org")}`}
                className="lg:hidden shrink-0 [&>*]:h-8 [&>*]:w-8 [&>*]:rounded-lg [&>*]:text-xs">{communityMark}</button>
            )}
            {inCommunityMode && !isOperator && communityPicked && (portfolioName || tenant?.company_name) ? (
              <span className="hidden lg:flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground min-w-0" data-testid="header-crumb">
                {showExitToPortfolio && actingCompany ? (
                  <button type="button" data-testid="header-crumb-portfolio" onClick={exitCommunity} title={exitLabel}
                    className="truncate max-w-[22rem] uppercase hover:text-ink transition-colors">{portfolioName || tenant?.company_name}</button>
                ) : (
                  <span className="truncate max-w-[22rem]">{portfolioName || tenant?.company_name}</span>
                )}
                <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-50" strokeWidth={2} />
                <span className="truncate text-ink" data-testid="header-crumb-community">{tenant?.name}</span>
              </span>
            ) : (
              <span className={`text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground truncate ${inCommunityMode && !isOperator ? "hidden lg:inline" : ""}`} data-testid="header-crumb">
                {isOperator ? "Operator Desk" : inCommunityMode ? (communityPicked ? (tenant?.name || `${V.one("org")}`) : `Your ${V.lcs("org")}`) : actingCompany ? <><span className="text-ink/45">Portfolio · </span>{portfolioName || "Portfolio"}</> : "Redline Company"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {!isOperator && quickSearchOn && (
            <button data-testid="cmdk-trigger" onClick={() => setPaletteOpen(true)} title="Search or jump to… (⌘K / Ctrl+K)"
              className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white p-2 sm:p-2.5 md:px-3.5 md:py-2 text-sm text-ink/60 hover:border-ink hover:text-ink transition-colors shrink-0 md:w-56 lg:w-72">
              <Search className="h-4.5 w-4.5 md:h-4 md:w-4 shrink-0" strokeWidth={1.75} />
              <span className="hidden md:inline flex-1 text-left truncate">{inCommunityMode ? "Search or jump to…" : "Jump to…"}</span>
              <kbd className="hidden md:inline rounded-md border border-sand-200 bg-sand-50 px-1.5 py-0.5 text-[10px] font-bold text-ink/70">⌘K</kbd>
            </button>
          )}
          {showExitToPortfolio && (
            <button data-testid="header-exit-community" onClick={exitCommunity} title={exitLabel}
              className="lg:hidden shrink-0 inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-2.5 sm:px-3 py-2 text-sm font-semibold text-ink hover:border-ink transition-colors">
              <ArrowLeft className="h-4 w-4" strokeWidth={2} /> <span className="hidden sm:inline">Exit</span>
            </button>
          )}
          {realIsSuper && !isOperator && !viewCompany && !inCommunityMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="redline-view-as-pill"
                  title="Super admin — tap to preview any seat"
                  className="flex items-center gap-1.5 sm:gap-2 rounded-full border px-2.5 sm:px-3.5 py-2 text-sm font-semibold shrink-0 min-w-0 transition-colors bg-white border-sand-200 text-ink hover:border-ink">
                  <Eye className="h-4 w-4 shrink-0 text-redline-500" strokeWidth={2} />
                  <span className="hidden md:inline text-[10px] uppercase tracking-[0.12em] font-bold opacity-60">Viewing as</span>
                  <span className="hidden sm:inline max-w-32 sm:max-w-60 truncate">Super admin</span>
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[22rem]">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b border-sand-100">
                  Preview the console exactly as each seat sees it — fully interactive. Switch back anytime, right here.
                </div>
                <DropdownMenuItem data-testid="redline-view-as-super">
                  <Eye className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> Super admin <span className="text-muted-foreground ml-1">(you)</span>
                  <Check className="h-4 w-4 ml-auto text-redline-500" />
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="redline-view-as-principal" onClick={() => setSeatPick("principal")}>
                  <Landmark className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> Administrator of a portfolio…
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="redline-view-as-pm" onClick={() => setSeatPick("pm")}>
                  <UserCog className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> {V.one("org")} Manager… <span className="text-muted-foreground ml-1">(pick portfolio, then their {V.lcs("org")})</span>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="redline-view-as-board" onClick={() => setSeatPick("board")}>
                  <Users className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> {V.one("board")} of…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isSuper && !!viewCompany && (!inCommunityMode || portfolioPreview) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="portfolio-view-as-pill"
                  title={portfolioPreview ? "Viewing as a Community Manager — tap to switch" : "Administrator view — tap to preview as a Community Manager"}
                  className={`flex items-center gap-1.5 sm:gap-2 rounded-full border px-2.5 sm:px-3.5 py-2 text-sm font-semibold shrink-0 min-w-0 transition-colors ${
                    portfolioPreview ? "bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200" : "bg-white border-sand-200 text-ink hover:border-ink"}`}>
                  <Eye className={`h-4 w-4 shrink-0 ${portfolioPreview ? "text-amber-600" : "text-redline-500"}`} strokeWidth={2} />
                  <span className="hidden md:inline text-[10px] uppercase tracking-[0.12em] font-bold opacity-60">Viewing as</span>
                  <span className="max-w-28 sm:max-w-60 truncate">{portfolioPreview ? `${V.one("org")} Manager · ${portfolioPersona.tenant_ids?.length || 0} ${V.lcs("org")}` : "Administrator"}</span>
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b border-sand-100">
                  Preview {viewCompany?.name || "this portfolio"} as each seat sees it — fully interactive.
                </div>
                <DropdownMenuItem data-testid="portfolio-view-as-principal" onClick={() => choosePortfolioPersona(null)}>
                  <Landmark className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> Administrator <span className="text-muted-foreground ml-1">(whole portfolio)</span>
                  {!portfolioPreview && <Check className="h-4 w-4 ml-auto text-redline-500" />}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="portfolio-view-as-manager" onClick={() => setPortfolioPickerOpen(true)}>
                  <UserCog className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> {V.one("org")} Manager… <span className="text-muted-foreground ml-1">(pick their {V.lcs("org")})</span>
                  {portfolioPreview && <Check className="h-4 w-4 ml-auto text-amber-600" />}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="portfolio-view-as-exit-super" onClick={() => { setPortfolioPersona(null); setViewCompany(null); setActiveTenantId(null); refreshTenants(); navigate("/dashboard/communities"); }}>
                  <Eye className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> Super admin <span className="text-muted-foreground ml-1">(back to the Redline console)</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {(canViewAsUser || viewAsUser) && !portfolioPreview && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="view-as-pill"
                  title={viewAsUser ? `Viewing as ${PERSONA_LABELS[viewPersona]} — tap to switch` : "Super admin — tap to view as another role"}
                  aria-label={viewAsUser ? `Viewing as ${PERSONA_LABELS[viewPersona]}` : "Super admin view"}
                  className={`flex items-center gap-1.5 sm:gap-2 rounded-full border px-2.5 sm:px-3.5 py-2 text-sm font-semibold shrink-0 min-w-0 transition-colors ${
                    viewAsUser
                      ? "bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200"
                      : "bg-white border-sand-200 text-ink hover:border-ink"
                  }`}>
                  <Eye className={`h-4 w-4 shrink-0 ${viewAsUser ? "text-amber-600" : "text-redline-500"}`} strokeWidth={2} />
                  <span className="hidden md:inline text-[10px] uppercase tracking-[0.12em] font-bold opacity-60">Viewing as</span>
                  <span className={`${viewAsUser ? "inline" : "hidden sm:inline"} max-w-20 sm:max-w-36 truncate`}>{viewAsUser ? PERSONA_LABELS[viewPersona] : "Super admin"}</span>
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b border-sand-100">
                  Preview {tenant?.name} exactly as each role sees it — fully interactive. Switch back anytime, right here.
                </div>
                <DropdownMenuItem data-testid="exit-view-as-btn" onClick={() => setViewPersona(null)}>
                  <Eye className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> Super admin <span className="text-muted-foreground ml-1">(you)</span>
                  {!viewAsUser && <Check className="h-4 w-4 ml-auto text-redline-500" />}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="view-as-admin-option" onClick={() => enableViewAs("hoa_manager")}>
                  <UserCog className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> {V.one("org")} admin
                  {viewPersona === "hoa_manager" && <Check className="h-4 w-4 ml-auto text-amber-600" />}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="view-as-board-option" onClick={() => enableViewAs("board_member")}>
                  <Users className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> {V.one("board")}
                  {viewPersona === "board_member" && <Check className="h-4 w-4 ml-auto text-amber-600" />}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="view-as-resident-option" onClick={openResidentPicker}>
                  <UserRound className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} /> {V.one("person")} (portal)…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {inCommunityMode && communityPicked && tenant?.phone_number && (
            <div className="hidden sm:flex items-center gap-2 text-sm text-ink-700 bg-white border border-sand-200 rounded-full px-4 py-2 shrink-0">
              <Phone className="h-4 w-4 text-redline-500" strokeWidth={1.5} /> {tenant.phone_number}
            </div>
          )}
          {!isOperator && (
            <button data-testid="header-account-gear" onClick={() => navigate("/dashboard/account")}
              title="Account settings"
              className="rounded-full border border-sand-200 bg-white p-2 sm:p-2.5 text-ink hover:border-ink transition-colors shrink-0">
              <Settings className="h-4.5 w-4.5" strokeWidth={1.75} />
            </button>
          )}
          {!isOperator && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notif-bell" className="relative rounded-full border border-sand-200 bg-white p-2 sm:p-2.5 hover:border-ink transition-colors shrink-0">
                  <Bell className="h-4.5 w-4.5 text-ink" strokeWidth={1.75} />
                  {unread > 0 && (
                    <span data-testid="notif-unread-badge" className="absolute -top-1 -right-1 min-w-4.5 h-4.5 px-1 rounded-full bg-redline-500 text-white text-[10px] font-bold flex items-center justify-center">{unread > 99 ? "99+" : unread}</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0">
                <div className="px-4 py-3 border-b border-sand-100 font-heading font-bold text-sm flex items-center justify-between">
                  <span>Notifications</span>
                  {unread > 0 && (
                    <button data-testid="notif-mark-all-read-btn" onClick={markAllRead} className="text-xs font-semibold text-ink-500 hover:text-ink">Mark all read</button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                {inCommunityMode && counts.copilot_inbox && (counts.mail_decisions || 0) > 0 && (
                  // Copilot Inbox ON: the bell points to Mail — one surface for everything that needs you.
                  <div data-testid="notif-inbox-pinned" onClick={() => navigate("/dashboard/mail?kind=decisions")}
                    className="w-full text-left px-4 py-3 border-b border-sand-100 bg-ink text-white hover:bg-ink/90 transition-colors cursor-pointer">
                    <div className="text-sm font-semibold flex items-start gap-2">
                      <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-redline-300" />
                      <span className="flex-1"><span data-testid="notif-inbox-count" className="font-bold">{counts.mail_decisions}</span> decision{counts.mail_decisions === 1 ? "" : "s"} need{counts.mail_decisions === 1 ? "s" : ""} you in the Mailbox</span>
                    </div>
                    <div className="text-xs text-white/70 mt-0.5 pl-6">Requests, conversations, approvals and more — open Mail to Ask or Handle each one.</div>
                  </div>
                )}
                {notifs.length === 0 ? (
                  <div data-testid="notif-empty" className="px-4 py-8 text-center text-sm text-muted-foreground">{counts.copilot_inbox && (counts.mail_decisions || 0) > 0 ? "Nothing else — everything that needs you is in the Mailbox." : "You're all caught up."}</div>
                ) : (
                  notifGroups.map((g) => (
                    g.items.length > 1 && !expandedTypes.includes(g.type) ? (
                      // A run of the same kind of alert collapses into one line — eight identical
                      // "resident awaiting approval" rows made the bell useless as a work queue.
                      <div key={g.key} data-testid={`notif-group-${g.type}`}
                        className={`w-full text-left px-4 py-3 border-b border-sand-100 last:border-0 hover:bg-sand-50 transition-colors cursor-pointer ${g.unread ? "bg-redline-50/40" : ""}`}
                        onClick={() => goToNotif(g.items[0])}>
                        <div className="text-sm font-semibold text-ink flex items-start gap-2">
                          {g.unread > 0 && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-redline-500 shrink-0" />}
                          <span className="flex-1">{g.items.length} × {g.label}</span>
                          <button data-testid={`notif-group-expand-${g.type}`}
                            onClick={(e) => { e.stopPropagation(); setExpandedTypes((t) => [...t, g.type]); }}
                            className="shrink-0 text-[10px] font-semibold text-ink/40 hover:text-ink border border-sand-200 rounded-full px-2 py-0.5">
                            Show each
                          </button>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{g.items[0].body}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          Latest {new Date(g.items[0].created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </div>
                      </div>
                    ) : g.items.map((n) => (
                    <div key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => goToNotif(n)}
                      className={`w-full text-left px-4 py-3 border-b border-sand-100 last:border-0 hover:bg-sand-50 transition-colors cursor-pointer ${n.read ? "" : "bg-redline-50/40"}`}>
                      <div className="text-sm font-semibold text-ink flex items-start gap-2">
                        {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-redline-500 shrink-0" />}
                        <span className="flex-1">{n.title}</span>
                        {!n.read && (
                          <button data-testid={`notif-mark-read-${n.id}`} title="Mark as read"
                            onClick={(e) => { e.stopPropagation(); markOneRead(n.id); }}
                            className="shrink-0 text-[10px] font-semibold text-ink/40 hover:text-redline-600 border border-sand-200 hover:border-redline-300 rounded-full px-2 py-0.5 transition-colors">
                            Mark read
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 pl-0">{n.body}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                    </div>
                    ))
                  ))
                )}
                </div>
                <DropdownMenuItem data-testid="notif-settings-link" onSelect={() => navigate("/dashboard/account#notifications")}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-t border-sand-100 text-xs font-semibold text-ink/55 hover:text-ink cursor-pointer focus:bg-sand-50">
                  <Settings className="h-3.5 w-3.5" strokeWidth={1.75} /> Notification settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          </div>
        </header>

        {canCustomize && activeGroups && (
          <CustomizeMenuDialog
            open={customizeOpen} onOpenChange={setCustomizeOpen}
            groups={activeGroups} initialItems={arrangement} defaultItems={defaultArrangement}
            onSave={saveNavLayout} saving={savingLayout}
            preset={inCommunityMode ? navPreset : null} presetSource={navPresetSource}
            groupsFor={(p) => (p === "v2" ? COMMUNITY_GROUPS_V2 : COMMUNITY_GROUPS)}
            defaultsFor={defaultArrangementFor}
            isSuper={isSuper && !viewAsUser} platformDefault={platformNavDefault} onPlatformDefault={savePlatformNavDefault}
            quickSearchOn={quickSearchOn}
          />
        )}
        {!isOperator && quickSearchOn && (
          <CommandPalette
            open={paletteOpen} onOpenChange={setPaletteOpen}
            tenantId={inCommunityMode ? contextTenantId : null}
            pages={[...navItems, ...toolItems].map((n) => ({ to: n.to, end: n.end, icon: n.icon, label: navLabel(n), badge: badgeOf(n), tone: n.tone,
              group: (activeGroups || []).find((g) => (arrangement?.[g] || []).some((x) => x.to === n.to)) || "" }))}
            tenants={canSwitchTenant ? switchableTenants : []} activeTenantId={inCommunityMode ? contextTenantId : null}
            onSwitchTenant={(id) => { setActiveTenantId(id); navigate("/dashboard"); }}
            actions={[
              ...(inCommunityMode && navItems.some((n) => n.to === "/dashboard/copilot") ? [{ id: "waiting", label: "What's waiting on me?", icon: Sparkles, to: `/dashboard/copilot?ask=${encodeURIComponent("What's waiting on me right now?")}` }] : []),
              ...(inCommunityMode && effRole !== "board_member" && navItems.some((n) => n.to === "/dashboard/actions") ? [{ id: "new-request", label: "New request", icon: ClipboardList, to: "/dashboard/actions" }] : []),
              ...(inCommunityMode && navItems.some((n) => n.to === "/dashboard/broadcasts") ? [{ id: "broadcast", label: "Send a message", icon: MessageSquareText, to: "/dashboard/broadcasts" }] : []),
              ...(inCommunityMode && effRole !== "board_member" && navItems.some((n) => n.to === "/dashboard/signatures") ? [{ id: "esign", label: "Send a document for signature", icon: PenLine, to: "/dashboard/signatures" }] : []),
              ...(inCommunityMode && effRole !== "board_member" && navItems.some((n) => n.to === "/dashboard/emergency") ? [{ id: "drill", label: "Run an emergency drill", icon: Siren, to: "/dashboard/emergency?drill=1" }] : []),
              ...(canCustomize ? [{ id: "organize", label: "Organize menu", icon: SlidersHorizontal, run: () => setCustomizeOpen(true) }] : []),
              { id: "account", label: "My account", icon: UserRound, to: "/dashboard/account" },
              { id: "logout", label: "Log out", icon: LogOut, run: logout },
            ]}
          />
        )}

        <Dialog open={residentPickOpen} onOpenChange={setResidentPickOpen}>
          <DialogContent className="max-w-md" data-testid="resident-picker-dialog">
            <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><UserRound className="h-5 w-5 text-redline-500" /> View portal as {V.a("person")}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground -mt-1">
              Pick {V.a("person")} of <span className="font-semibold text-ink">{tenant?.name}</span>. The portal opens in a new tab with a
              4-hour session — everything works exactly as it does for them, and the visit is logged in the Activity Log.
            </p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
              <input data-testid="resident-picker-search" value={pickQ} onChange={(e) => setPickQ(e.target.value)} placeholder="Search by name, unit or phone…" autoFocus
                className="w-full rounded-xl border border-sand-200 pl-9 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            </div>
            <div className="max-h-72 overflow-y-auto -mx-1 px-1 space-y-1" data-testid="resident-picker-list">
              {pickHouseholds === null ? (
                <div className="flex justify-center py-10"><div className="h-6 w-6 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
              ) : (() => {
                const s = pickQ.trim().toLowerCase();
                const rows = pickHouseholds.flatMap((h) => (h.people || []).map((p) => ({ p, h })))
                  .filter(({ p, h }) => !s || (p.name || "").toLowerCase().includes(s) || (h.unit || "").toLowerCase().includes(s) || (p.phone || "").includes(s));
                if (!rows.length) return <div className="text-center py-8 text-sm text-ink/40">{pickHouseholds.length ? `No ${V.lcs("person")} match your search.` : `No ${V.lcs("person")} on the roster yet.`}</div>;
                return rows.slice(0, 60).map(({ p, h }) => (
                  <button key={p.id} data-testid={`resident-pick-${p.id}`} disabled={impersonating} onClick={() => impersonateResident(p)}
                    className="w-full flex items-center gap-3 rounded-xl border border-sand-200 bg-white hover:border-redline-300 hover:bg-redline-50/40 transition-colors px-3 py-2.5 text-left disabled:opacity-50">
                    <div className="h-8 w-8 rounded-full bg-ink text-white flex items-center justify-center text-xs font-bold shrink-0">{(p.name || "?").trim().charAt(0).toUpperCase()}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-ink truncate">{p.name || `Unnamed ${V.lc("person")}`}</div>
                      <div className="text-xs text-ink/50 truncate">{[homeLabel(h), p.phone].filter(Boolean).join(" · ") || "No address / phone on file"}</div>
                    </div>
                    <Eye className="h-4 w-4 text-ink/30 shrink-0" strokeWidth={1.75} />
                  </button>
                ));
              })()}
            </div>
          </DialogContent>
        </Dialog>

        <main className="p-4 lg:p-8 overflow-x-hidden">
          {smsSuper && smsStatus && !smsStatus.sending_live && !quietPage && !smsBannerHidden && (
            <div data-testid="sms-off-banner" className="mb-4 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" strokeWidth={2.2} />
              <div className="text-xs leading-snug min-w-0 flex-1">
                <span className="font-bold text-red-800">Text sending is off</span>{" "}
                <span className="text-red-700/90">
                  {smsStatus.env_kill_switch
                    ? "in this preview environment — codes, alerts and concierge replies are generated but not delivered. Production sends normally."
                    : `— the SMS master switch is off. Turn it back on under ${V.a("org")}'s Concierge settings → Number & connection check.`}
                </span>
              </div>
              <button data-testid="sms-off-banner-dismiss" onClick={() => { sessionStorage.setItem("askredline.smsBannerHidden", "1"); setSmsBannerHidden(true); }} title="Hide for this session"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-red-600 hover:bg-red-100 shrink-0"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
          {isSuper && viewCompany && !inCommunityMode && !portfolioPreview && (
            <div data-testid="view-company-banner" className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-2xl bg-indigo-600 text-white px-4 py-3">
              <div className="flex items-center gap-2 text-sm">
                <Landmark className="h-4 w-4 text-indigo-200 shrink-0" strokeWidth={2} />
                <span>You're viewing <span className="font-bold">{viewCompany.name}</span>'s portfolio as a super admin. You can manage everything.</span>
              </div>
              <button data-testid="exit-view-company-btn" onClick={exitCompanyView}
                className="shrink-0 rounded-full bg-white text-indigo-700 font-semibold text-sm px-4 py-1.5 hover:bg-indigo-50 transition-colors">
                Exit to portfolios
              </button>
            </div>
          )}
          {portfolioPreview && !viewAsUser && (
            <div data-testid="portfolio-view-as-hint" className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-2 text-xs text-amber-800">
              <Eye className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
              <span>Previewing <span className="font-bold">{viewCompany?.name || "this portfolio"}</span> as a <span className="font-bold">Community Manager</span> assigned to {portfolioPersona.tenant_ids?.length || 0} {portfolioPersona.tenant_ids?.length === 1 ? "community" : "communities"} — use the amber pill above to switch back.</span>
            </div>
          )}
          {viewAsUser && (
            <div data-testid="view-as-hint" className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-2 text-xs text-amber-800">
              <Eye className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
              <span>Previewing as <span className="font-bold">{PERSONA_LABELS[viewPersona]}</span> — use the amber pill above to switch back.</span>
            </div>
          )}
          {inCommunityMode && !viewAsUser && emStatus?.enabled && emStatus?.on_ladder && !emStatus?.phone_verified && !quietPage && (
            <button data-testid="emergency-verify-phone-banner" onClick={() => navigate("/dashboard/account#verify")}
              className="mb-5 w-full text-left rounded-xl border border-redline-200 bg-redline-50 px-4 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 hover:bg-redline-100/70 transition-colors group">
              <span className="flex items-center gap-2.5 min-w-0 flex-1">
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-redline-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-redline-500" />
                </span>
                <Siren className="h-4 w-4 text-redline-600 shrink-0" strokeWidth={2} />
                <span className="text-sm min-w-0">
                  <span className="font-bold text-danger-900">You're on the emergency response ladder</span>
                  <span className="text-redline-800"> — but your mobile isn't verified, so you won't get text or call alerts. Verify it now.</span>
                </span>
              </span>
              <span className="shrink-0 self-end sm:self-auto inline-flex items-center gap-1 text-redline-900 text-sm font-bold group-hover:gap-2 transition-all">
                Verify <ChevronDown className="h-4 w-4 -rotate-90" />
              </span>
            </button>
          )}
          {showProfileNudge && !quietPage && (
            <div data-testid="profile-completeness-banner"
              className="mb-5 w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
              <span className="flex items-start gap-2.5 min-w-0 flex-1">
                <UserRound className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={2} />
                <span className="text-sm min-w-0">
                  <span className="font-bold text-amber-900">Finish setting up your account</span>
                  <span className="text-amber-800"> — {profileGaps.join(" · ")}.</span>
                </span>
              </span>
              <span className="shrink-0 self-end sm:self-auto flex items-center gap-1.5">
                <button data-testid="profile-completeness-fix" onClick={() => navigate("/dashboard/account#verify")}
                  className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700 transition-colors">
                  Fix now
                </button>
                <button data-testid="profile-completeness-dismiss" onClick={snoozeProfileNudge} title="Remind me in a week"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-amber-700 hover:bg-amber-100 transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </span>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" />
            </div>
          ) : blockedCommunityPage ? (
            <div className="flex items-center justify-center h-64" data-testid="custom-role-redirecting">
              <div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" />
            </div>
          ) : (
            <Outlet key={vocabKey} context={{ tenantId: contextTenantId, tenant, tenants: switchableTenants, allTenants: tenants, setTenantId: setActiveTenantId, enterCommunity, exitCommunity, role: effRole, actualRole: role, viewAsUser, viewPersona, refreshTenants, viewCompany, viewCompanyId: viewCompany?.id || null, enterCompanyView, canConfigure: !!user?.can_configure, me: user?.user_id, meUser: user, canCustomizeMenu: canCustomize, openCustomizeMenu: () => setCustomizeOpen(true) }} />
          )}
        </main>
      </div>
      <OnboardingWizard tenantId={contextTenantId} active={inCommunityMode && communityPicked && !viewAsUser}
        replayNonce={replayTour} onReplayHandled={() => setReplayTour(0)} />
      <PortfolioManagerPicker open={portfolioPickerOpen} onOpenChange={setPortfolioPickerOpen} company={viewCompany}
        initial={portfolioPersona?.tenant_ids || []} onChoose={(ids) => choosePortfolioPersona({ tenant_ids: ids })} />
      <SeatPicker open={!!seatPick} onOpenChange={(o) => { if (!o) setSeatPick(null); }} seat={seatPick} onChoose={chooseSeat} />
      <PortfolioManagerPicker open={!!pmCompany} onOpenChange={(o) => { if (!o) setPmCompany(null); }} company={pmCompany}
        initial={[]} onChoose={choosePmCommunities} />
    </div>
  );
}
